import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Spinner } from 'react-bootstrap';
import {
  useLazyGetYourDayBriefingQuery,
  useLazyGetYourDayWeekReviewQuery,
  useLazyGetYourDayMailboxesQuery,
  useScanYourDayMailboxesMutation,
  useAddYourDayMailboxMutation,
  useRemoveYourDayMailboxMutation,
  useGetSlackStatusQuery,
  useConnectSlackMutation,
  useDisconnectSlackMutation,
  useAskGlanceMutation,
  useGetMyPreferencesQuery,
  useGetAdminConfigQuery,
  useLazyGetMailFoldersQuery,
  useMoveMessageMutation,
  useDismissBriefingMessageMutation,
  useUndismissBriefingMessageMutation,
} from '../app/api';
import { useIsMobile } from './utils';
import Tasks from './Tasks';
import {
  msalInstance, graphMailRequest, graphSharedMailRequest,
  graphMailWriteRequest, graphSharedMailWriteRequest, setMsalReturnTo,
} from '../msalConfig';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
  amber:     '#b7791f',
};

const PATH = '/your-day';

// Tier presentation. Order here is the render order within the tier stack.
const TIERS = [
  {
    key: 'tier1',
    label: 'Requires Your Attention',
    dot: brand.red,
    blurb: 'Client issues, decisions, signatures, deadlines.',
    collapsedByDefault: false,
    collapsedOnMobile: false,
  },
  {
    key: 'tier2',
    label: 'Can Be Delegated or Batched',
    dot: brand.amber,
    blurb: 'Real work, but it does not need you specifically.',
    collapsedByDefault: false,
    // On a phone this is scroll between the user and nothing they must act on.
    collapsedOnMobile: true,
  },
  {
    key: 'tier3',
    label: 'No Action Needed',
    dot: brand.green,
    blurb: 'Newsletters, notifications, FYI copies.',
    collapsedByDefault: true,
    collapsedOnMobile: true,
  },
];

// Tier colour and name by key, for rows shown OUTSIDE a tier heading — the
// Slack tab regroups by conversation, and without these it would silently drop
// the briefing's judgement about what matters. Derived from TIERS rather than
// restated, so a colour or label only ever changes in one place. 'unclassified'
// is not a tier and has no entry; the lookups fall back for it.
const TIER_DOTS = Object.fromEntries(TIERS.map((t) => [t.key, t.dot]));
const TIER_LABELS = Object.fromEntries(TIERS.map((t) => [t.key, t.label]));

// The Slack tab's sections, in the order they earn attention. Keys match the
// `reason` the backend sets on each item (integrations/slack.py) — why a
// message reached you is what decides its tier, so it is also the sensible way
// to group a browse of them.
const SLACK_SECTIONS = [
  {
    key: 'dm', label: 'Direct messages', icon: 'bi-chat-dots-fill',
    blurb: 'Sent to you personally.',
  },
  {
    key: 'mention', label: 'Mentions', icon: 'bi-at',
    blurb: 'You were named in a channel.',
  },
  {
    key: 'thread', label: 'Thread replies', icon: 'bi-reply-all-fill',
    blurb: 'Threads you were already part of.',
  },
];

// Minimum touch target. 44px is the Apple/WCAG floor; anything smaller is a
// coin-flip with a thumb.
const TAP = 44;

// The three views. Splitting them is presentation only — all three queries still
// fire together on page load, so Week is already built by the time
// anyone clicks across. That's the point: the slowest of the three (Week, which
// reasons over seven days of mail) gets its generation time back while the user
// reads today, instead of making them watch a spinner on a page they opened to
// see this morning's inbox.
// `short` is what a phone gets: three tabs plus their counts don't fit across a
// 390px screen at a readable size, and "Your" is the word carrying the least.
const TABS = [
  { key: 'day',   label: 'Your Day',  short: 'Day',   icon: 'bi-sunrise-fill' },
  { key: 'week',  label: 'Your Week', short: 'Week',  icon: 'bi-journal-text' },
  // Slack, on its own. Not a replacement for having Slack in the tiers — the
  // same messages are in both, and the tiers are where the day's priorities get
  // decided. This is for the other question people ask: "what has happened in
  // Slack", which is a browse rather than a triage, so it groups by
  // conversation the way Slack itself does. Appears only for someone who has
  // actually connected an account; there is nothing to browse otherwise.
  { key: 'slack', label: 'Slack',     short: 'Slack', icon: 'bi-slack' },
  // Tasks is the third view rather than a sibling page: what's in my inbox,
  // what happened this week, and what I actually have to do are the same
  // question asked three ways, and splitting the third onto its own menu row
  // made people go looking for it. It renders the full Tasks page embedded —
  // one implementation, still reachable on its own at /tasks.
  { key: 'tasks', label: 'Tasks',     short: 'Tasks', icon: 'bi-check2-square' },
];

// Counts shown on the tabs. 'urgent' is reserved for Tier 1 — if everything can
// be red then nothing is.
const BADGE_TONES = {
  active: { background: 'rgba(255,255,255,0.22)', color: 'white',          borderColor: 'transparent' },
  urgent: { background: 'rgba(192,57,43,0.12)',   color: brand.red,        borderColor: `${brand.red}55` },
  quiet:  { background: brand.softGray,           color: brand.deepBlue,   borderColor: 'transparent' },
};

// Week in Review theme categories. Keys must match the enum in
// integrations/bedrock_briefing.py `_THEME_CATEGORIES`; the backend coerces
// anything else to 'operations', so there is no unknown-key case to design for.
const CATEGORY_COLORS = {
  client:     brand.tealBlue,
  financial:  brand.green,
  compliance: brand.red,
  operations: brand.charcoal,
  technology: brand.deepBlue,
  people:     brand.amber,
  external:   brand.goldenrod,
};

// Long unbroken subjects and sender addresses are the norm in email. Without
// both of these, one of them forces horizontal scroll on the whole page.
const WRAP = { minWidth: 0, overflowWrap: 'anywhere' };

// Every "open this email" link on the page shares one named tab, so working a
// briefing top to bottom reuses a single Outlook tab instead of leaving a dozen
// behind. Note the deliberate omission of rel="noopener noreferrer" at the call
// sites: either token makes the browser ignore the target name and open a fresh
// context anyway, which is exactly the behaviour we're removing. The tradeoff is
// that the tab gets a cross-origin window.opener handle on us — acceptable for
// outlook.office365.com, and not something to copy for untrusted destinations.
const OUTLOOK_TAB = 'pcaOutlook';

// Slack's own reused tab, for the same reason and with the same caveat as the
// Outlook one. Deliberately a DIFFERENT name: sharing one tab would mean
// opening a Slack message navigated away from a half-written mail reply.
const SLACK_TAB = 'pcaSlack';

// A row is mail unless it says otherwise. Rows generated before Slack existed
// carry no `source` at all, and the briefing's mail rows still don't — treating
// absence as mail is what let Slack ship without touching how mail renders.
function isSlack(row) {
  return row?.source === 'slack';
}

// Tooltip on the Slack chip. Mirrors the reasons the backend sets in
// integrations/slack.py — why this message reached you is the thing that
// justifies its tier, and it isn't obvious from a channel name.
const SLACK_REASONS = {
  dm: 'Sent to you directly',
  mention: 'You were @-mentioned',
  thread: 'A reply in a thread you were in',
};

// Compact pill used by the calendar-shape strip.
function chip(color) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
    background: `${color}14`, color, border: `1px solid ${color}40`,
    borderRadius: 999, padding: '0.15rem 0.5rem', fontWeight: 700,
    whiteSpace: 'nowrap',
  };
}

function minutesLabel(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  return h ? `${h}h` : `${m}m`;
}

function clockLabel(mins) {
  const h24 = Math.floor(mins / 60);
  const m = mins % 60;
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h24 < 12 ? 'AM' : 'PM'}`;
}

// Age of a silent thread. Days up to a fortnight, then weeks — "23 days" reads
// as precision the number doesn't have.
function ageLabel(days) {
  const d = Math.round(days);
  if (d <= 1) return 'yesterday';
  if (d < 14) return `${d} days`;
  return `${Math.floor(d / 7)} weeks`;
}

const cardStyle = {
  background: 'white',
  border: 'none',
  borderRadius: 12,
  boxShadow: '0 2px 12px rgba(10,41,71,0.08)',
  overflow: 'hidden',
};

// Desktop day-timeline geometry.
const HOUR_H = 54;          // px per hour
const GUTTER_W = 52;        // hour-label column
const MIN_EVENT_H = 20;     // so a 15-minute meeting is still readable
const DEFAULT_FROM = 8;     // always show at least 8 AM …
const DEFAULT_TO = 18;      // … through 6 PM, expanded to fit outliers

/** Lay overlapping events into side-by-side columns.
 *
 * Without this, two meetings at the same time render exactly on top of each other
 * and one is invisible — the failure mode is silent, which is the worst kind on a
 * calendar. Greedy: walk events in start order and drop each into the first column
 * whose last event has already ended.
 */
function packColumns(events) {
  const cols = [];
  const placed = events.map((e) => {
    let idx = cols.findIndex((endsAt) => endsAt <= e.startMin);
    if (idx === -1) { cols.push(e.endMin); idx = cols.length - 1; }
    else cols[idx] = e.endMin;
    return { ...e, col: idx };
  });
  return { placed, colCount: Math.max(1, cols.length) };
}

/** Outlook-style vertical day grid: hour rows, events positioned by time. */
function DayTimeline({ events, nowMinutes }) {
  // Advance the "now" marker while the page sits open, anchored to the server's
  // value so it stays in the firm's zone rather than the viewer's.
  const [drift, setDrift] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setDrift((d) => d + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const allDay = events.filter((e) => e.is_all_day);
  const timed = events
    .filter((e) => !e.is_all_day && Number.isFinite(e.start_minutes))
    .map((e) => ({
      ...e,
      startMin: e.start_minutes,
      // Guarantee a non-zero span even if end is missing or malformed.
      endMin: Number.isFinite(e.end_minutes) && e.end_minutes > e.start_minutes
        ? e.end_minutes
        : e.start_minutes + 30,
    }))
    .sort((a, b) => a.startMin - b.startMin);

  // Events with no usable time can't be placed — list them rather than drop them.
  const untimed = events.filter((e) => !e.is_all_day && !Number.isFinite(e.start_minutes));

  const fromHour = Math.min(DEFAULT_FROM, ...timed.map((e) => Math.floor(e.startMin / 60)));
  const toHour = Math.max(DEFAULT_TO, ...timed.map((e) => Math.ceil(e.endMin / 60)));
  const originMin = fromHour * 60;
  const hours = [];
  for (let h = fromHour; h <= toHour; h++) hours.push(h);
  const gridH = (toHour - fromHour) * HOUR_H;

  const { placed, colCount } = packColumns(timed);
  const nowTop = Number.isFinite(nowMinutes)
    ? ((nowMinutes + drift - originMin) / 60) * HOUR_H
    : null;
  const nowVisible = nowTop !== null && nowTop >= 0 && nowTop <= gridH;

  const hourLabel = (h) => {
    const suffix = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12} ${suffix}`;
  };

  return (
    <div>
      {allDay.length > 0 && (
        <div style={{ marginBottom: '0.5rem' }}>
          {allDay.map((e, i) => (
            <div key={`ad-${i}`} style={{
              background: brand.softGray, borderLeft: `3px solid ${brand.goldenrod}`,
              borderRadius: 6, padding: '0.3rem 0.5rem', marginBottom: '0.25rem',
              fontSize: '0.78rem', color: brand.deepBlue, fontWeight: 700, ...WRAP,
            }}>
              All day · {e.subject}
            </div>
          ))}
        </div>
      )}

      <div style={{ position: 'relative', height: gridH }}>
        {/* Hour rows */}
        {hours.slice(0, -1).map((h, i) => (
          <div key={h} style={{
            position: 'absolute', top: i * HOUR_H, left: 0, right: 0, height: HOUR_H,
            borderTop: `1px solid ${brand.softGray}`,
          }}>
            <span style={{
              position: 'absolute', top: -8, left: 0, width: GUTTER_W - 8,
              textAlign: 'right', fontSize: '0.7rem', color: brand.charcoal,
            }}>
              {hourLabel(h)}
            </span>
          </div>
        ))}
        {/* Closing line so the last hour row is bounded */}
        <div style={{ position: 'absolute', top: gridH, left: 0, right: 0, borderTop: `1px solid ${brand.softGray}` }}>
          <span style={{ position: 'absolute', top: -8, left: 0, width: GUTTER_W - 8, textAlign: 'right', fontSize: '0.7rem', color: brand.charcoal }}>
            {hourLabel(toHour)}
          </span>
        </div>

        {/* Now marker */}
        {nowVisible && (
          <div style={{ position: 'absolute', top: nowTop, left: GUTTER_W - 4, right: 0, zIndex: 3, pointerEvents: 'none' }}>
            <div style={{ height: 2, background: brand.tealBlue }} />
            <div style={{ position: 'absolute', top: -3, left: -4, width: 8, height: 8, borderRadius: '50%', background: brand.tealBlue }} />
          </div>
        )}

        {/* Events */}
        {placed.map((e, i) => {
          const top = ((e.startMin - originMin) / 60) * HOUR_H;
          const height = Math.max(MIN_EVENT_H, ((e.endMin - e.startMin) / 60) * HOUR_H - 2);
          const colW = `calc((100% - ${GUTTER_W}px) / ${colCount})`;
          const compact = height < 34;
          return (
            <div
              key={e.id || `${e.subject}-${i}`}
              title={`${e.start_label}–${e.end_label} ${e.subject}`}
              style={{
                position: 'absolute', top, height,
                left: `calc(${GUTTER_W}px + (${colW} * ${e.col}))`,
                width: colW,
                background: 'rgba(0,105,166,0.12)',
                borderLeft: `3px solid ${brand.tealBlue}`,
                borderRadius: 4, padding: compact ? '0 0.35rem' : '0.2rem 0.35rem',
                overflow: 'hidden', zIndex: 2, ...WRAP,
              }}
            >
              <div style={{
                color: brand.deepBlue, fontWeight: 700, fontSize: '0.74rem',
                lineHeight: 1.25, whiteSpace: compact ? 'nowrap' : 'normal',
                textOverflow: 'ellipsis', overflow: 'hidden',
              }}>
                {e.subject}
              </div>
              {!compact && (
                <div style={{ color: brand.charcoal, fontSize: '0.68rem', lineHeight: 1.2 }}>
                  {e.start_label}{e.end_label ? `–${e.end_label}` : ''}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {untimed.length > 0 && (
        <div style={{ marginTop: '0.6rem', fontSize: '0.75rem', color: brand.charcoal, ...WRAP }}>
          {untimed.length} event{untimed.length === 1 ? '' : 's'} with no readable time:{' '}
          {untimed.map((e) => e.subject).join(', ')}
        </div>
      )}
    </div>
  );
}

DayTimeline.propTypes = {
  events: PropTypes.arrayOf(PropTypes.shape({
    subject: PropTypes.string,
    is_all_day: PropTypes.bool,
    start_label: PropTypes.string,
    end_label: PropTypes.string,
    start_minutes: PropTypes.number,
    end_minutes: PropTypes.number,
  })).isRequired,
  nowMinutes: PropTypes.number,
};

DayTimeline.defaultProps = {
  nowMinutes: undefined,
};

/** "3 min ago" for the last-refreshed line.
 *
 * Relative rather than a clock time on purpose: the useful question is "is what
 * I'm reading stale?", and a relative figure answers it without raising the
 * whose-timezone question that a wall-clock time would — the rest of this page
 * deliberately renders times in the FIRM's zone, not the viewer's, so an absolute
 * "Updated 9:52 AM" here would be quietly inconsistent for anyone travelling.
 */
function relativeTime(then, now) {
  if (!then) return '';
  const mins = Math.max(0, Math.round((now - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins === 1) return '1 min ago';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hrs < 24) return rem ? `${hrs} hr ${rem} min ago` : `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

function hubEmail() {
  return window.localStorage.getItem('e') || '';
}

// ── Week in Review session cache ────────────────────────────────────────────
//
// RTK Query's cache is in memory, so it dies on every page reload — which meant a
// handful of reloads burned through the hourly generation cap for something that
// summarizes seven days and barely changes within a working session.
//
// Cached in sessionStorage specifically, not localStorage: it holds AI-derived
// summaries of client mail, so it should evaporate when the tab closes rather than
// persist on disk. That's the same store MSAL already uses for tokens, and it's
// the user's own mail in the user's own browser — but it is deliberately the
// narrowest option that actually survives a reload. A server-side cache would
// survive more, and would put that content in MySQL, which is the books-and-records
// question still open with Compliance.
//
// The morning briefing is deliberately NOT cached this way: it reasons about the
// current time, so a copy restored at 2pm would be giving 9am advice.
const WEEK_CACHE_TTL_MS = 60 * 60 * 1000;

// Keyed by MAILBOX as well as by person. Sharing one key across mailboxes would
// have shown service@'s week under someone's own name after a reload, which is
// the worst kind of wrong — plausible, and about a mailbox they didn't pick.
function weekCacheKey(mailbox) {
  return `yourday:week:${hubEmail().toLowerCase()}:${(mailbox || 'me').toLowerCase()}`;
}

function readWeekCache(mailbox) {
  try {
    const raw = sessionStorage.getItem(weekCacheKey(mailbox));
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry?.ts || !entry?.payload) return null;
    if (Date.now() - entry.ts > WEEK_CACHE_TTL_MS) return null;
    return entry;
  } catch (e) {
    return null; // private mode, quota, or corrupt JSON — just regenerate
  }
}

function writeWeekCache(payload, mailbox) {
  try {
    sessionStorage.setItem(
      weekCacheKey(mailbox),
      JSON.stringify({ ts: Date.now(), mailbox: mailbox || '', payload }),
    );
  } catch (e) { /* storage unavailable; caching is best-effort */ }
}

function clearWeekCache(mailbox) {
  try { sessionStorage.removeItem(weekCacheKey(mailbox)); } catch (e) { /* ignore */ }
}

/** Resolve the MSAL account for the signed-in hub user, or null.
 *
 * `setActiveAccount` is never called anywhere in this app, so getActiveAccount()
 * can be null on a reloaded session — match on the email the hub session stored
 * instead.
 *
 * Deliberately returns null rather than guessing when there's no email match.
 * Plenty of people here hold two Microsoft identities (@prosperitycapitaladvisors
 * and @c2penterprises); falling back to "whichever account is first" would
 * acquire a token for the wrong mailbox, and the server would then reject it as
 * an identity mismatch — which reads to the user like the feature is broken.
 */
function resolveAccount() {
  const email = hubEmail().toLowerCase();
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) return null;
  const match = accounts.find(a => (a.username || '').toLowerCase() === email);
  if (match) return match;
  // Only safe to assume when there's exactly one identity and nothing to match on.
  return !email && accounts.length === 1 ? accounts[0] : null;
}

/** The interactive request, pinned to the hub identity.
 *
 * Passing `account` (or at minimum `loginHint`) is what stops Microsoft showing
 * the "Pick an account" chooser. Without it, a user with two work accounts gets a
 * picker they have no way to answer correctly — the hub session already decided
 * which identity this is, so the prompt is both avoidable and misleading.
 */
function interactiveRequest(scopes = graphMailRequest) {
  const account = resolveAccount();
  if (account) return { ...scopes, account };
  const email = hubEmail();
  return email ? { ...scopes, loginHint: email } : { ...scopes };
}

export default function YourDay() {
  const isMobile = useIsMobile();
  // Two-column only when there's genuinely room for it. Between 769 and 1100px a
  // 340px sidebar would leave the mail column too narrow to read comfortably, so
  // that band keeps the stacked desktop layout.
  const isNarrow = useIsMobile(1100);
  // The mailbox rail costs 220px, and the day view already spends 340 on the
  // schedule column. Below this the rail would squeeze the mail column narrower
  // than the two-column layout was designed for, so the dropdown is used
  // instead — the choice is the same, it just doesn't take the width.
  const railTooNarrow = useIsMobile(1320);
  const [trigger, { data, isFetching, isError, error, fulfilledTimeStamp }] = useLazyGetYourDayBriefingQuery();
  // Fired from the same token acquisition as the briefing, so there's only ever
  // one MSAL round-trip per page load and the two Bedrock calls run in parallel.
  const [triggerWeek, week] = useLazyGetYourDayWeekReviewQuery();

  // Filing mail into a folder. Hidden entirely unless an admin has enabled it —
  // showing a control that always 403s is worse than not showing one. The admin
  // config query is already in flight app-wide (Root.jsx), so this is free.
  const { data: adminConfig } = useGetAdminConfigQuery();
  const canFileMail = adminConfig?.config?.enable_mail_move === true;
  // `!== false` rather than `=== true`: the ask box defaults ON, so an
  // unloaded config must not hide a feature that works — the same reasoning
  // the tab-availability flags use further down.
  const askEnabled = adminConfig?.config?.enable_glance_ask !== false;
  const [loadFolders, foldersRes] = useLazyGetMailFoldersQuery();
  const [moveMessageReq] = useMoveMessageMutation();
  const [dismissReq] = useDismissBriefingMessageMutation();
  const [undismissReq] = useUndismissBriefingMessageMutation();

  // ── Which mailbox ────────────────────────────────────────────────────────
  // '' = the reader's own, which is the state everyone who has no second
  // mailbox stays in forever. Every piece of mailbox machinery below is inert
  // in that state, which is what keeps this feature from costing anything to
  // the majority who never touch it.
  const [mailbox, setMailbox] = useState('');
  const [loadMailboxes, mailboxesRes] = useLazyGetYourDayMailboxesQuery();
  const [scanMailboxesReq] = useScanYourDayMailboxesMutation();
  const [addMailboxReq] = useAddYourDayMailboxMutation();
  const [removeMailboxReq] = useRemoveYourDayMailboxMutation();
  // True while the tenant-wide probe is running. Shown, but never blocking:
  // the menu is usable from cache throughout, and on a first visit it holds
  // just "Your mailbox" until the scan lands.
  const [scanning, setScanning] = useState(false);
  // True once we know a shared token can't be had silently — the rail then
  // offers a button instead of pretending the menu is still loading. Consent
  // has to come from a click, so this is a state, not a retry.
  const [needsSharedConsent, setNeedsSharedConsent] = useState(false);
  const [mailboxError, setMailboxError] = useState('');
  const [showAddMailbox, setShowAddMailbox] = useState(false);
  const [newMailbox, setNewMailbox] = useState('');
  const [savingMailbox, setSavingMailbox] = useState(false);
  // oldMessageId -> { newId, folder }. Rows in here are hidden from their tier and
  // replaced by a confirmation with an Undo, which is why we keep the new id: the
  // move invalidated the id the briefing was built with.
  const [filed, setFiled] = useState({});
  // Messages marked done in this session. Same shape and same reasoning as
  // `filed`: the row stays put with an Undo rather than vanishing under the
  // reader's eye. Unlike `filed`, the server remembers — the row is gone from
  // tomorrow's briefing, and its slot is filled by the next message down.
  const [done, setDone] = useState({});
  const [dismissing, setDismissing] = useState(null);
  const [filing, setFiling] = useState('');
  const [fileError, setFileError] = useState('');

  // ── Slack ────────────────────────────────────────────────────────────────
  // Wholly separate from the Microsoft machinery above and deliberately so:
  // this is the person's SLACK account, granted once through Slack's own
  // consent screen, and no Graph token is involved. The items themselves are
  // not fetched here — they arrive already tiered inside the briefing, which is
  // the whole design (one ranked list, not two).
  const { data: slackStatus, refetch: refetchSlack } = useGetSlackStatusQuery();
  const [connectSlackReq] = useConnectSlackMutation();
  const [disconnectSlackReq] = useDisconnectSlackMutation();
  const [slackBusy, setSlackBusy] = useState(false);
  const [slackError, setSlackError] = useState('');
  // Set from the `?slack=` the OAuth callback redirects back with, so somebody
  // who just finished (or cancelled) the round trip gets told what happened.
  // The briefing they are looking at was built before they connected, so the
  // notice says to refresh rather than pretending Slack is already in it.
  const [slackNotice, setSlackNotice] = useState('');

  // ── Ask a question ───────────────────────────────────────────────────────
  // A mutation, not a query: it is an action somebody takes and it spends AI
  // tokens, so it must never be re-fired by a remount or a cache refetch.
  const [askReq, askRes] = useAskGlanceMutation();
  const [askDraft, setAskDraft] = useState('');
  // The last answer, held here rather than read from the mutation result so it
  // survives the next question being typed — and so Clear can drop it without
  // resetting the mutation.
  const [answer, setAnswer] = useState(null);
  const [askError, setAskError] = useState('');

  // 'loading' → resolving a Graph token; 'connect' → needs interactive consent;
  // 'ready' → a briefing request has been fired.
  const [phase, setPhase] = useState('loading');
  const [authError, setAuthError] = useState('');
  // Guards the Connect button while Microsoft has a window open. Clicking twice
  // is what produces MSAL's "interaction_in_progress" error, and then every
  // subsequent click fails the same way — the loop Susan hit.
  const [connecting, setConnecting] = useState(false);
  // interaction_in_progress leaves a flag in MSAL's session storage that only
  // `handleRedirectPromise` clears — which runs at app boot. So the honest remedy
  // is a page reload, offered as a button rather than left as a dead end.
  const [needsReload, setNeedsReload] = useState(false);
  const [openTiers, setOpenTiers] = useState({});
  const [showUnclassified, setShowUnclassified] = useState(false);
  // Which view is on screen. Always starts on the day — it's what the page is
  // named for, and it's the one that's ready first.
  const [tab, setTab] = useState('day');
  // Week in Review themes start rolled up to their titles. Keyed by index+title
  // rather than index alone, so a refreshed review with different themes doesn't
  // inherit the previous one's expansion state by position.
  const [openThemes, setOpenThemes] = useState({});
  // Ticks so the "Updated N min ago" line ages while the page sits open — a
  // briefing left up all morning shouldn't keep claiming it's fresh. Holding the
  // clock itself (rather than a counter) keeps it an honestly-used value.
  const [now, setNow] = useState(() => Date.now());
  // A week review restored from sessionStorage, used when RTK Query has nothing
  // (i.e. after a reload). Seeded synchronously so the tile never flashes empty.
  // Carries its mailbox so a restored copy is only ever shown under the mailbox
  // it was built from.
  const [weekCache, setWeekCache] = useState(() => readWeekCache(''));
  // The zone the user has chosen in the sidebar settings dialog. Read here only
  // to notice when a cached briefing was built in a different one.
  const { data: prefs } = useGetMyPreferencesQuery();
  const rebuiltForTz = useRef(null);
  const started = useRef(false);

  /** Silent-first token acquisition.
   *
   * MSAL caches in sessionStorage while the hub session lives in localStorage, so
   * a restored tab routinely has a valid hub session and no MSAL account at all.
   * That makes the interactive path common rather than exceptional — especially on
   * iOS, which discards background tabs aggressively. It cannot be an automatic
   * retry either: the interactive call has to come from a user gesture or the
   * browser blocks it. So a silent miss surfaces a button.
   */
  /* A token carrying the `.Shared` scopes.
   *
   * ALWAYS goes through acquireTokenSilent, even when one was obtained a moment
   * ago. MSAL owns the caching and the refresh; holding a token ourselves would
   * work perfectly for an hour and then start handing expired bearers to Graph
   * on a page somebody left open all morning — which is exactly the kind of
   * failure that looks like a permissions bug.
   *
   * `interactive` is the difference between "find out whether we have consent"
   * (page load: a miss just shows a button, because consent must come from a
   * click) and "get consent now" (the click).
   */
  const loadSharedToken = useCallback(async ({ interactive = false } = {}) => {
    const account = resolveAccount();
    if (!account && !interactive) return null;
    const req = account
      ? { ...graphSharedMailRequest, account }
      : interactiveRequest(graphSharedMailRequest);
    try {
      const r = await msalInstance.acquireTokenSilent(req);
      setNeedsSharedConsent(false);
      return r.accessToken;
    } catch (e) {
      if (!interactive) {
        setNeedsSharedConsent(true);
        return null;
      }
      // Popup on desktop, redirect on mobile — same reasoning as `connect`
      // below: iOS Safari is where popups fail and where this path runs most.
      if (isMobile) {
        setMsalReturnTo(PATH);
        await msalInstance.acquireTokenRedirect(req);
        return null;                          // navigating away
      }
      const r = await msalInstance.acquireTokenPopup(req);
      setNeedsSharedConsent(false);
      return r.accessToken;
    }
  }, [isMobile]);

  /* The token to send with a mailbox read.
   *
   * Shared and delegated mailboxes need the `.Shared` scopes — with one
   * exception. In demo mode the server answers every one of these endpoints
   * from fixtures and opens no mailbox at all, so the token is carried and
   * ignored. Asking Microsoft to consent to reading other people's mail, for a
   * token nothing will use, would put a consent dialog in front of a room
   * mid-demo and end with the mailbox rail showing a button instead of the
   * mailboxes it is there to demonstrate. `data.demo` is set only by the
   * fixtures (see api/app/demo_data.py), so no live briefing reaches this.
   */
  const demoMode = data?.demo === true;
  const mailboxToken = useCallback(async ({ shared = false, interactive = false } = {}) => {
    if (shared && !demoMode) return loadSharedToken({ interactive });
    const account = resolveAccount();
    if (!account) return null;
    const r = await msalInstance.acquireTokenSilent({ ...graphMailRequest, account });
    return r.accessToken;
  }, [demoMode, loadSharedToken]);

  /* Ask a question.
   *
   * Reuses the briefing's READ scopes — the mail search, the calendar window
   * and Slack are all reads, so nobody gets a fresh consent prompt for typing a
   * question. Silent acquisition only: every path here starts with a click, but
   * a click that opens a Microsoft popup in the middle of typing a question is
   * a worse experience than an error saying to reload, and by this point the
   * briefing has already proved the token works.
   */
  const submitAsk = useCallback(async () => {
    const question = askDraft.trim();
    if (question.length < 3 || askRes.isLoading) return;
    setAskError('');
    const account = resolveAccount();
    if (!account) {
      setAskError('Reconnect Outlook and try again.');
      return;
    }
    try {
      const token = await mailboxToken({ shared: !!mailbox });
      if (!token) throw new Error('no token');
      const res = await askReq({
        graphToken: token, mailbox: mailbox || null, question,
      }).unwrap();
      setAnswer(res);
    } catch (e) {
      // 429 has its own wording: "try again in a while" is actionable, "could
      // not answer" sends somebody looking for a fault that isn't there.
      setAskError(
        e?.status === 429
          ? (e?.data?.detail || 'That’s a lot of questions. Please try again in a while.')
          : e?.status === 400
            ? (e?.data?.detail || 'Please try a different question.')
            : 'Could not answer that. Please try again.'
      );
    }
  }, [askDraft, askRes.isLoading, askReq, mailbox, mailboxToken]);

  const runBriefing = useCallback(async ({ preferCache = true, box = '' } = {}) => {
    const account = resolveAccount();
    if (!account) {
      setPhase('connect');
      return;
    }
    try {
      // A shared mailbox needs the `.Shared`-scoped token; own mail deliberately
      // does not, so the majority never meet its consent prompt. The shared set
      // is a superset and would serve both — but asking for it when there is no
      // shared mailbox in play would defeat the point of keeping them apart.
      const token = await mailboxToken({ shared: !!box });
      if (!token) throw new Error(box ? 'shared consent required' : 'no token');
      setAuthError('');
      setPhase('ready');
      trigger({ graphToken: token, mailbox: box || null }, preferCache);
      // Only ask for a new week review when there isn't a fresh one to hand. A
      // forced refresh always regenerates; a plain page load reuses.
      const cached = preferCache ? readWeekCache(box) : null;
      if (cached) {
        setWeekCache(cached);
      } else {
        if (!preferCache) { clearWeekCache(box); }
        setWeekCache(null);
        triggerWeek({ graphToken: token, mailbox: box || null }, preferCache);
      }
    } catch (e) {
      // A silent miss on the SHARED scopes is a different problem from a silent
      // miss on the base ones: the reader's own briefing is fine, only the
      // shared mailbox needs consent. Surfacing "Connect Outlook" over a
      // working page would read as the whole feature having broken.
      if (box) {
        setNeedsSharedConsent(true);
        setMailboxError('Microsoft needs your permission to open other mailboxes.');
        setMailbox('');
        return;
      }
      // Any silent failure lands on the same remedy — ask the user to connect.
      // Surface the reason underneath so a misconfiguration is diagnosable
      // instead of looking like an endless consent loop.
      if (e?.errorCode && e.errorCode !== 'interaction_required' && e.errorCode !== 'login_required'
          && e.errorCode !== 'consent_required' && e.errorCode !== 'no_account_error') {
        setAuthError(e.errorMessage || e.message || '');
      }
      setPhase('connect');
    }
  }, [trigger, triggerWeek, mailboxToken]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    runBriefing({ preferCache: true });
  }, [runBriefing]);

  useEffect(() => {
    // 30s, so "just now" gives way to "1 min ago" without a visible lag.
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  /* Read the `?slack=` the OAuth callback sent us back with.
   *
   * The callback can't render anything — it's a redirect from slack.com — so the
   * outcome has to travel in the URL and be turned into a message here. The
   * parameter is then STRIPPED with replaceState, because a reload of
   * `?slack=connected` would otherwise re-announce a connection made an hour
   * ago, and because the state token is spent.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('slack');
    if (!result) return;
    const reason = params.get('reason') || '';
    if (result === 'connected') {
      // The briefing on screen was built BEFORE this connection, so it contains
      // no Slack. Saying "refresh to include it" is the honest version of what
      // would otherwise look like a feature that silently did nothing.
      setSlackNotice('Slack connected. Refresh to pull it into this briefing.');
      refetchSlack();
    } else if (result === 'cancelled') {
      setSlackNotice('Slack was not connected.');
    } else {
      setSlackNotice(
        reason === 'storage'
          ? 'Slack can’t be connected in this environment — ask IT to check the hub’s encryption key.'
          : 'Slack could not be connected. Please try again.'
      );
    }
    window.history.replaceState({}, '', window.location.pathname);
  }, [refetchSlack]);

  /* Send the browser to Slack's consent screen.
   *
   * A full-page redirect rather than a popup, and unlike the Microsoft flow
   * there is no silent path to try first: Slack has no equivalent of MSAL's
   * cached token, so connecting is always an explicit round trip. Popups are
   * also what mobile Safari blocks, which is the same reason the MSAL flow
   * falls back to a redirect on phones.
   */
  const connectSlack = useCallback(async () => {
    setSlackBusy(true);
    setSlackError('');
    try {
      const res = await connectSlackReq().unwrap();
      if (!res?.url) throw new Error('no url');
      window.location.href = res.url;
    } catch (e) {
      setSlackBusy(false);
      setSlackError(
        e?.status === 503
          ? 'Slack isn’t set up in this environment yet.'
          : 'Could not start the Slack connection. Please try again.'
      );
    }
  }, [connectSlackReq]);

  const disconnectSlack = useCallback(async () => {
    // Confirmed because it is not reversible without another trip through
    // Slack's consent screen, and because the button sits next to a briefing
    // somebody is reading rather than in a settings page they came to on
    // purpose.
    if (!window.confirm('Disconnect Slack? Your briefings will go back to email only.')) return;
    setSlackBusy(true);
    setSlackError('');
    try {
      await disconnectSlackReq().unwrap();
      await refetchSlack();
      setSlackNotice('Slack disconnected.');
    } catch (e) {
      setSlackError('Could not disconnect Slack. Please try again.');
    } finally {
      setSlackBusy(false);
    }
  }, [disconnectSlackReq, refetchSlack]);

  /* Load the mailbox menu.
   *
   * Runs SILENT-ONLY and never blocks the briefing. The order matters: the own
   * mailbox briefing is already in flight by the time this fires, so someone
   * with no shared mailboxes sees exactly the page they saw before this feature
   * existed — no extra prompt, no extra spinner, and no rail.
   *
   * A silent miss on the `.Shared` scopes is expected rather than exceptional
   * (they're consented separately, and MSAL's cache dies with the tab), so it
   * sets `needsSharedConsent` and the rail offers a button. Consent must come
   * from a click, so there is nothing to retry here.
   */
  const sharedMailboxesOn = adminConfig?.config?.enable_shared_mailboxes !== false;
  // Which MODE the menu was last loaded in, not just whether it has been. The
  // first pass fires before the briefing has answered, so a demo is still
  // unknown and the pass runs as a real one; when the fixtures come back saying
  // otherwise, the menu is loaded again without the shared-consent step.
  const mailboxesStarted = useRef(null);

  /* Run the tenant-wide probe, then reload the menu.
   *
   * Backgrounded and never awaited by anything the reader is waiting on. It
   * takes a couple of seconds and the page is already useful without it, so the
   * only visible effect is the rail gaining entries part-way through — which is
   * better than a spinner where the mail should be.
   */
  const runScan = useCallback(async (token) => {
    setScanning(true);
    try {
      await scanMailboxesReq({ graphToken: token }).unwrap();
      await loadMailboxes({ graphToken: token }, false).unwrap();
    } catch (e) {
      // Deliberately silent. Discovery is an improvement to a menu; failing to
      // improve it is not something to put a banner about in front of somebody
      // reading their mail. The server logs it, and the next visit retries.
    } finally {
      setScanning(false);
    }
  }, [scanMailboxesReq, loadMailboxes]);

  useEffect(() => {
    if (!sharedMailboxesOn || phase !== 'ready') return;
    const mode = demoMode ? 'demo' : 'real';
    if (mailboxesStarted.current === mode) return;
    mailboxesStarted.current = mode;
    (async () => {
      const token = await mailboxToken({ shared: true });
      if (!token) return;                     // rail will offer the button
      // A demo needs no consent, so clear anything the first pass concluded
      // before it knew that — otherwise the rail offers a button nobody needs
      // to press, over a menu that is already loading.
      if (demoMode) { setNeedsSharedConsent(false); setMailboxError(''); }
      try {
        const res = await loadMailboxes({ graphToken: token }, true).unwrap();
        // The server decides when a scan is due — never scanned, or older than
        // the configured window. Keeping that decision server-side means one
        // rule rather than one per client.
        if (res?.needs_scan) runScan(token);
      } catch (e) {
        // The rail simply doesn't appear. A failed menu must not disturb the
        // briefing that already rendered above it.
        setMailboxError(e?.data?.detail || '');
      }
    })();
  }, [sharedMailboxesOn, phase, demoMode, mailboxToken, loadMailboxes, runScan]);

  const rescan = useCallback(async () => {
    setMailboxError('');
    const token = await mailboxToken({ shared: true, interactive: true });
    if (token) await runScan(token);
  }, [mailboxToken, runScan]);

  /* Switch mailbox.
   *
   * Fires a briefing AND a week review for the new mailbox, both `preferCache`,
   * so coming back to one already generated is instant and spends no Bedrock
   * call — which is what makes the menu usable against a default limit of six
   * briefings an hour.
   */
  const selectMailbox = useCallback(async (address) => {
    const box = (address || '').toLowerCase();
    if (box === mailbox) return;
    setMailboxError('');
    // Session state that belongs to the mailbox we're leaving. Filed/done rows
    // are keyed by message id, which is mailbox-specific, so carrying them over
    // is harmless — but the folder picker and its error are not, and a stale
    // "could not move" banner over a different mailbox is confusing.
    setFileError('');
    // A half-typed question goes too. It was aimed at the mailbox being left,
    // and the answer it would get is the new mailbox's — the same wrong-mailbox
    // mix-up `shownAnswer` guards for an answer already on screen, except this
    // one would be the reader's own words handed to the wrong search. The
    // ANSWER itself is not dropped: it stays gated on the mailbox it was asked
    // of, so switching back shows it again rather than re-spending a call.
    setAskDraft('');
    setAskError('');
    setMailbox(box);
    if (box) {
      // Consent is asked for HERE, on the click, rather than left to the
      // briefing — the gesture is what the browser requires. In demo mode
      // `mailboxToken` answers from the base token instead, so a demonstrator
      // clicking a fictional mailbox never sees Microsoft at all.
      const token = await mailboxToken({ shared: true, interactive: true });
      if (!token) { setMailbox(''); return; }  // consent declined or redirecting
    }
    await runBriefing({ preferCache: true, box });
  }, [mailbox, mailboxToken, runBriefing]);

  const addMailbox = useCallback(async () => {
    const address = newMailbox.trim().toLowerCase();
    if (!address) return;
    setSavingMailbox(true);
    setMailboxError('');
    try {
      // The token goes up with the add so the server can ask Exchange whether
      // this person can actually open it — saving an unopenable mailbox and
      // letting them discover it on the next click is a poor way to find out.
      const token = await mailboxToken({ shared: true, interactive: true });
      if (!token) return;
      await addMailboxReq({ address, graphToken: token }).unwrap();
      setNewMailbox('');
      setShowAddMailbox(false);
      await loadMailboxes({ graphToken: token }, false).unwrap();
      await selectMailbox(address);
    } catch (e) {
      setMailboxError(e?.data?.detail || 'That mailbox could not be added.');
    } finally {
      setSavingMailbox(false);
    }
  }, [newMailbox, addMailboxReq, loadMailboxes, mailboxToken, selectMailbox]);

  const removeMailbox = useCallback(async (address) => {
    setMailboxError('');
    try {
      await removeMailboxReq({ address }).unwrap();
      // Don't leave the reader looking at a mailbox that is no longer in the
      // menu; fall back to their own before refreshing the list.
      if (address === mailbox) await selectMailbox('');
      // Silent-only: the row is already gone server-side, so a missing token is
      // no reason to interrupt someone with a Microsoft prompt. Worst case the
      // menu still shows the entry until the next page load.
      const token = await mailboxToken({ shared: true });
      if (token) await loadMailboxes({ graphToken: token }, false).unwrap();
    } catch (e) {
      setMailboxError(e?.data?.detail || 'That mailbox could not be removed.');
    }
  }, [removeMailboxReq, loadMailboxes, mailboxToken, mailbox, selectMailbox]);

  // Persist each freshly generated week review so the next reload in this tab
  // doesn't spend another generation on it.
  useEffect(() => {
    if (week.data && week.fulfilledTimeStamp) {
      // `week.data.mailbox` rather than the `mailbox` state: the response says
      // which mailbox it was actually built from, and the state may already have
      // moved on if someone switched while it was generating. Caching it under
      // the wrong key is exactly the mix-up the per-mailbox key exists to stop.
      const built = week.data.mailbox || '';
      writeWeekCache(week.data, built);
      setWeekCache({ ts: week.fulfilledTimeStamp, mailbox: built, payload: week.data });
    }
  }, [week.data, week.fulfilledTimeStamp]);

  // Change your time zone in the sidebar and the cached briefing is now wrong by
  // hours — every label in it, plus the coverage window itself. Rebuild rather
  // than leave someone reading a schedule in the zone they just left.
  // `rebuiltForTz` makes this fire once per change, not once per render.
  useEffect(() => {
    const want = prefs?.effective_timezone;
    const built = data?.timezone;
    if (!want || !built || want === built) return;
    if (rebuiltForTz.current === want) return;
    rebuiltForTz.current = want;
    runBriefing({ preferCache: false, box: mailbox });
  }, [prefs?.effective_timezone, data?.timezone, runBriefing, mailbox]);

  /** Interactive consent. Popup on desktop, redirect on mobile.
   *
   * acquireTokenPopup is exactly what mobile Safari blocks or renders unusable, and
   * mobile is where this path fires most. The redirect leaves the app and returns
   * through /auth-redirect, so leave a breadcrumb — AuthRedirect otherwise assumes
   * every redirect is a fresh login and would strand the user on the dashboard.
   */
  const connect = useCallback(async () => {
    // A second click while Microsoft's window is open is exactly what triggers
    // interaction_in_progress, after which every retry fails identically.
    if (connecting) return;
    setConnecting(true);
    setAuthError('');

    const request = interactiveRequest();
    const goRedirect = () => {
      setMsalReturnTo(PATH);
      return msalInstance.acquireTokenRedirect(request);
    };

    try {
      if (isMobile) {
        // iOS Safari blocks or mangles popups, and it's where this path fires most.
        await goRedirect();
        return; // navigating away; nothing further runs
      }
      const result = await msalInstance.acquireTokenPopup(request);
      setPhase('ready');
      trigger({ graphToken: result.accessToken }, false);
      triggerWeek({ graphToken: result.accessToken }, false);
    } catch (e) {
      const code = e?.errorCode || '';
      if (code === 'user_cancelled') {
        // Not an error — they closed the window. Leave them on the connect screen
        // without an alarming banner.
      } else if (code === 'interaction_in_progress') {
        setNeedsReload(true);
        setAuthError(
          'A Microsoft sign-in window was already open. Reload the page and try once more.'
        );
      } else if (
        code === 'popup_window_error' || code === 'empty_window_error'
        // block_nested_popups means this window IS a leftover sign-in popup. A
        // popup can't open a popup, but it CAN redirect itself — so the redirect
        // flow is the escape hatch rather than a dead end.
        || code === 'block_nested_popups'
      ) {
        try {
          await goRedirect();
          return;
        } catch (e2) {
          setAuthError(e2?.errorMessage || e2?.message || 'Could not reach Microsoft.');
        }
      } else {
        setAuthError(e?.errorMessage || e?.message || 'Outlook was not connected.');
      }
    } finally {
      setConnecting(false);
    }
  }, [connecting, isMobile, trigger, triggerWeek]);

  // Refresh means "rebuild what I'm looking at", so it stays on the selected
  // mailbox rather than quietly dropping back to the reader's own.
  const refresh = useCallback(
    () => runBriefing({ preferCache: false, box: mailbox }), [runBriefing, mailbox]);

  const briefing = data?.briefing;
  const events = data?.events || [];
  const counts = data?.counts;
  const actions = briefing?.priority_actions || [];
  // Deterministic tiles — computed server-side from the same fetch, no model
  // call. Absent when an admin has switched `enable_day_extras` off, or when
  // building them failed; either way the tiles just don't render.
  const extras = data?.extras || null;
  const shape = extras?.calendar || null;
  const awaiting = extras?.awaiting || [];
  const people = extras?.people || [];
  const unclassified = briefing?.unclassified || [];

  // Default open state depends on viewport, so it can't live in state — an
  // untouched tier follows the breakpoint, and an explicit tap wins from then on.
  const defaultOpen = (t) => (isMobile ? !t.collapsedOnMobile : !t.collapsedByDefault);
  const tierOpen = (t) => (t.key in openTiers ? openTiers[t.key] : defaultOpen(t));
  const toggleTier = (t) => setOpenTiers(prev => ({ ...prev, [t.key]: !(t.key in prev ? prev[t.key] : defaultOpen(t)) }));

  // ── Sections ─────────────────────────────────────────────────────────────

  const sectionCard = (children, extra) => (
    <div style={{ ...cardStyle, padding: isMobile ? '0.9rem' : '1.1rem 1.25rem', marginBottom: isMobile ? '0.75rem' : '1rem', ...extra }}>
      {children}
    </div>
  );

  const sectionTitle = (icon, text, right) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem', ...WRAP }}>
      <i className={`bi ${icon}`} style={{ color: brand.tealBlue, fontSize: '1rem' }} />
      <span style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '0.95rem', ...WRAP }}>{text}</span>
      {right}
    </div>
  );

  const priorityActionsSection = actions.length > 0 && sectionCard(
    <>
      {sectionTitle('bi-pin-angle-fill', "Today's Priority Actions")}
      <ol style={{ margin: 0, paddingLeft: '1.25rem', color: brand.deepBlue }}>
        {actions.map((a, i) => (
          <li key={i} style={{ marginBottom: '0.4rem', lineHeight: 1.45, fontSize: isMobile ? '0.95rem' : '0.92rem', ...WRAP }}>
            {a}
          </li>
        ))}
      </ol>
    </>,
    // The one thing worth acting on gets a visible edge on mobile, where it's the
    // first card in the viewport.
    { borderLeft: `4px solid ${brand.goldenrod}` }
  );

  // A briefing cached from before minute offsets existed (or any future payload
  // that loses them) would otherwise render an empty grid plus "no readable time"
  // for every event. Fall back to the list, which only needs the labels.
  const hasOffsets = events.some(e => Number.isFinite(e.start_minutes) || e.is_all_day);
  const useListView = isMobile || !hasOffsets;

  const scheduleSection = sectionCard(
    <>
      {sectionTitle('bi-calendar-week-fill', "Today's Schedule",
        <span style={{ marginLeft: 'auto', color: brand.charcoal, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
          {events.length} {events.length === 1 ? 'event' : 'events'}
        </span>
      )}
      {/* How the day actually sits. Inside the schedule card rather than a card
          of its own — it describes the schedule, and the page is already dense
          enough without another box. */}
      {shape && shape.meeting_count > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '0.35rem',
          marginBottom: '0.7rem', fontSize: '0.74rem',
        }}>
          <span style={chip(brand.tealBlue)}>
            <i className="bi bi-clock" /> {shape.meeting_label} in meetings
          </span>
          {shape.back_to_back > 0 && (
            <span style={chip(brand.amber)}>
              <i className="bi bi-arrow-bar-right" /> {shape.back_to_back} back-to-back
            </span>
          )}
          {shape.conflicts.length > 0 && (
            <span style={chip(brand.red)} title={shape.conflicts.map(c => c.subject).join(', ')}>
              <i className="bi bi-exclamation-triangle-fill" /> {shape.conflicts.length} double-booked
            </span>
          )}
          {shape.focus_blocks.map((b, i) => (
            <span key={i} style={chip(brand.green)}>
              <i className="bi bi-shield-check" /> {minutesLabel(b.minutes)} free at {clockLabel(b.start_minutes)}
            </span>
          ))}
        </div>
      )}
      {events.length === 0 ? (
        <div style={{ color: brand.charcoal, fontSize: '0.88rem' }}>Nothing scheduled.</div>
      ) : useListView ? (
        // Mobile stays a plain vertical list. An hour grid on a 390px-wide screen
        // would either squeeze subjects to nothing or make the page enormously
        // tall for a mostly-empty day.
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {events.map((e, i) => (
            <div key={i} style={{
              background: brand.softGray, borderRadius: 8,
              padding: '0.5rem 0.7rem', ...WRAP,
              flex: '1 1 auto',
              borderLeft: `3px solid ${brand.tealBlue}`,
            }}>
              <div style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '0.8rem' }}>
                {e.is_all_day
                  ? 'All day'
                  : e.start_label
                    ? `${e.start_label}–${e.end_label || ''}`
                    : 'Time unavailable'}
              </div>
              <div style={{ color: brand.charcoal, fontSize: '0.85rem', ...WRAP }}>{e.subject}</div>
              {e.location && (
                <div style={{ color: brand.charcoal, fontSize: '0.75rem', opacity: 0.8, ...WRAP }}>
                  <i className="bi bi-geo-alt" /> {e.location}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <DayTimeline events={events} nowMinutes={data?.now_minutes} />
      )}
    </>
  );

  // Waiting on a reply — threads where the user sent last and nothing came back.
  // Hidden entirely when empty: a permanent "nothing here" box trains people to
  // stop looking at the space.
  const awaitingSection = awaiting.length > 0 && sectionCard(
    <>
      {sectionTitle('bi-hourglass-split', 'Waiting on a Reply',
        <span style={{ marginLeft: 'auto', color: brand.charcoal, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
          {awaiting.length}
        </span>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
        {awaiting.map((a, i) => {
          const who = a.to.map(t => t.name || t.email).join(', ');
          const extra = a.to_total > a.to.length ? ` +${a.to_total - a.to.length}` : '';
          return (
            <a
              key={i}
              href={a.web_link || '#'}
              target={OUTLOOK_TAB}
              style={{
                display: 'block', textDecoration: 'none',
                background: brand.softGray, borderRadius: 8,
                borderLeft: `3px solid ${brand.amber}`,
                padding: '0.45rem 0.6rem', ...WRAP,
              }}
            >
              <div style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '0.82rem', ...WRAP }}>
                {a.subject}
              </div>
              <div style={{ color: brand.charcoal, fontSize: '0.75rem', ...WRAP }}>
                {who}{extra} · sent {ageLabel(a.age_days)} ago
              </div>
            </a>
          );
        })}
      </div>
      <div style={{ color: brand.charcoal, fontSize: '0.7rem', marginTop: '0.5rem', opacity: 0.8, ...WRAP }}>
        {/* Said plainly, because the tile is wrong in one direction and people
            should know which: a reply by phone or in person looks like silence. */}
        Last {extras?.awaiting_window_days} days. A reply by phone or in person still looks like silence here.
      </div>
    </>
  );

  // Birthdays and work anniversaries. Bottom of the page on purpose — it's the
  // one section that isn't work, and it should feel like a grace note.
  const peopleSection = people.length > 0 && sectionCard(
    <>
      {sectionTitle('bi-balloon-fill', 'Coming Up')}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
        {people.map((p, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', ...WRAP }}>
            <i
              className={`bi ${p.kind === 'birthday' ? 'bi-gift-fill' : 'bi-award-fill'}`}
              style={{ color: p.kind === 'birthday' ? brand.goldenrod : brand.tealBlue, fontSize: '0.8rem' }}
            />
            <span style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '0.82rem' }}>{p.name}</span>
            <span style={{ color: brand.charcoal, fontSize: '0.76rem', ...WRAP }}>
              {p.kind === 'birthday' ? 'birthday' : `${p.years} year${p.years === 1 ? '' : 's'}`}
              {' · '}
              {p.in_days === 0 ? 'today' : p.in_days === 1 ? 'tomorrow' : `in ${p.in_days} days`}
            </span>
          </div>
        ))}
      </div>
    </>
  );

  /* Slack state, read by three things: the strip below the header, the Slack
   * tab's availability, and the tab's own panel.
   *
   * Hidden entirely when the feature is off or unconfigured (`available`
   * false), so nothing about Slack appears for a firm that hasn't turned it on.
   * Also hidden on a shared mailbox: the briefing there is service@'s, and
   * Slack is nobody's shared property — see `_slack_activity` in the router.
   */
  const slackAvailable = slackStatus?.available === true && !mailbox;
  const slackRead = data?.slack || {};
  const slackConnected = slackStatus?.connected === true;
  const slackNeedsReconnect = slackRead.error === 'reconnect';

  /* Every Slack message the briefing tiered, pulled back out of the tiers.
   *
   * The Slack tab is a VIEW of the briefing, not a second fetch: these rows have
   * already been read, tiered and paid for, so the tab opens instantly, costs
   * nothing, and can never disagree with the priorities list about what arrived.
   * A separate Slack read would have bought a slightly fuller list at the price
   * of two sources of truth on one page.
   *
   * Each row keeps the tier it landed in, which is what the dot on the row
   * shows — the grouping changes to match how people think about Slack, the
   * judgement about urgency does not.
   */
  const slackRows = [];
  for (const key of ['tier1', 'tier2', 'tier3', 'unclassified']) {
    for (const row of briefing?.[key] || []) {
      if (isSlack(row)) slackRows.push({ ...row, tier: key });
    }
  }
  const slackBanner = (slackNotice || slackError) && (
    <div style={{
      ...cardStyle, padding: '0.6rem 0.9rem',
      marginBottom: isMobile ? '0.75rem' : '1rem',
      display: 'flex', gap: '0.5rem', alignItems: 'center',
      color: slackError ? brand.red : brand.deepBlue, fontSize: '0.83rem', ...WRAP,
    }}>
      <i className={`bi ${slackError ? 'bi-exclamation-triangle-fill' : 'bi-info-circle-fill'}`} />
      <span style={WRAP}>{slackError || slackNotice}</span>
      <button
        type="button"
        onClick={() => { setSlackNotice(''); setSlackError(''); }}
        aria-label="Dismiss"
        style={{
          background: 'none', border: 'none', marginLeft: 'auto',
          color: brand.charcoal, cursor: 'pointer', minHeight: isMobile ? TAP : 'auto',
        }}
      >
        <i className="bi bi-x-lg" />
      </button>
    </div>
  );

  // Week in Review — recall material for a weekly team meeting, which is why it
  // gets its own tab rather than a slot in a column about today. A 404 means an
  // admin switched `enable_week_review` off; the tab disappears with it, instead
  // of showing an error for something that isn't broken.
  // Live result if we have one, otherwise whatever survived the last reload.
  // Both sources are gated on the mailbox they were BUILT from, not the one
  // selected. `week.data` lags a mailbox switch by however long generation
  // takes, and the sessionStorage copy can be for any mailbox visited this
  // session — showing either under the wrong heading would be a
  // plausible-looking summary of somebody else's mail, which is the one failure
  // here worth designing out.
  const weekLive = week.data && (week.data.mailbox || '') === mailbox ? week.data : null;
  const weekRestored = weekCache && (weekCache.mailbox || '') === mailbox ? weekCache : null;
  const weekPayload = weekLive || weekRestored?.payload || null;
  const weekReview = weekPayload?.review;
  const weekGeneratedAt = (weekLive && week.fulfilledTimeStamp) || weekRestored?.ts || null;
  const weekOff = week.error?.status === 404;
  // 429 is not a failure — it means "generated very recently". Treated as its own
  // state so it never renders as a warning box, and so a cached copy simply shows
  // instead of an error where one exists.
  const weekThrottled = week.error?.status === 429;
  // "Fetching" isn't the only state with nothing to show. The tabs now go up as
  // soon as there's a token, so someone can be looking at this tab in the window
  // between that and `triggerWeek` firing, when the query is still uninitialised.
  // Both states have to read as "building", not as "nothing happened last week".
  const weekLoading = week.isFetching || (!weekPayload && !week.isError);
  // Lazy: only build it when it's on screen.
  const renderWeekCard = () => sectionCard(
    <>
      {sectionTitle('bi-journal-text', 'Week in Review',
        weekPayload?.counts && (
          <span style={{ marginLeft: 'auto', color: brand.charcoal, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
            {weekPayload.counts.themes} {weekPayload.counts.themes === 1 ? 'theme' : 'themes'}
          </span>
        )
      )}
      {weekPayload?.period_label && (
        <div style={{ color: brand.charcoal, fontSize: '0.75rem', marginTop: -4, marginBottom: '0.5rem', ...WRAP }}>
          {weekPayload.period_label}
          {weekGeneratedAt && ` · built ${relativeTime(weekGeneratedAt, now)}`}
        </div>
      )}

      {weekLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: brand.charcoal, fontSize: '0.85rem' }}>
          <Spinner animation="border" size="sm" style={{ color: brand.tealBlue }} />
          Reviewing the past week…
        </div>
      ) : week.isError && !weekThrottled && !weekReview ? (
        <Alert variant="warning" className="py-2 mb-0" style={{ fontSize: '0.82rem', ...WRAP }}>
          {week.error?.data?.detail || 'Could not build your Week in Review.'}
        </Alert>
      ) : weekThrottled && !weekReview ? (
        // Nothing is wrong — it was just generated and there's no local copy in
        // this tab. A muted line, not a warning box.
        <div style={{ color: brand.charcoal, fontSize: '0.82rem', ...WRAP }}>
          <i className="bi bi-clock-history me-1" />
          Just generated elsewhere — it&apos;ll be available again shortly.
        </div>
      ) : !weekReview || weekReview.themes.length === 0 ? (
        <div style={{ color: brand.charcoal, fontSize: '0.85rem', ...WRAP }}>
          Nothing significant surfaced in the past week.
        </div>
      ) : (
        <>
          {weekReview.headline && (
            <div style={{
              color: brand.deepBlue, fontSize: '0.88rem', fontWeight: 700,
              lineHeight: 1.45, marginBottom: '0.7rem', ...WRAP,
            }}>
              {weekReview.headline}
            </div>
          )}

          {weekReview.themes.map((t, i) => {
            const key = `${i}|${t.title}`;
            const open = Boolean(openThemes[key]);
            return (
              <div key={key} style={{
                borderTop: i === 0 ? 'none' : `1px solid ${brand.softGray}`, ...WRAP,
              }}>
                <button
                  type="button"
                  onClick={() => setOpenThemes((p) => ({ ...p, [key]: !p[key] }))}
                  aria-expanded={open}
                  style={{
                    width: '100%', minHeight: TAP, display: 'flex', alignItems: 'center',
                    gap: '0.45rem', padding: '0.4rem 0', background: 'none', border: 'none',
                    textAlign: 'left', cursor: 'pointer', ...WRAP,
                  }}
                >
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: CATEGORY_COLORS[t.category] || brand.charcoal,
                  }} />
                  <span style={{
                    flex: 1, minWidth: 0, color: brand.deepBlue,
                    fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.3, ...WRAP,
                  }}>
                    {t.title}
                  </span>
                  <i
                    className={`bi bi-chevron-${open ? 'up' : 'down'}`}
                    style={{ color: brand.charcoal, fontSize: '0.72rem', flexShrink: 0 }}
                  />
                </button>
                {open && (
                  <div style={{
                    color: brand.charcoal, fontSize: '0.82rem', lineHeight: 1.45,
                    paddingBottom: '0.55rem', paddingLeft: '0.95rem', ...WRAP,
                  }}>
                    {t.summary}
                  </div>
                )}
              </div>
            );
          })}

          {weekReview.decisions.length > 0 && (
            <div style={{ marginTop: '0.8rem', paddingTop: '0.6rem', borderTop: `1px solid ${brand.softGray}` }}>
              <div style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.3rem' }}>
                Decided
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.1rem', color: brand.charcoal, fontSize: '0.82rem' }}>
                {weekReview.decisions.map((d, i) => (
                  <li key={i} style={{ marginBottom: '0.2rem', lineHeight: 1.4, ...WRAP }}>{d}</li>
                ))}
              </ul>
            </div>
          )}

          {weekReview.open_items.length > 0 && (
            <div style={{ marginTop: '0.7rem', paddingTop: '0.6rem', borderTop: `1px solid ${brand.softGray}` }}>
              <div style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.3rem' }}>
                Still open — talking points
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.1rem', color: brand.charcoal, fontSize: '0.82rem' }}>
                {weekReview.open_items.map((o, i) => (
                  <li key={i} style={{ marginBottom: '0.2rem', lineHeight: 1.4, ...WRAP }}>{o}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </>
  );

  // The day-in-one-sentence read. Full width at the very top on every viewport —
  // it's the one line that frames everything below it, so it shouldn't be buried
  // under the calendar in a side column.
  const scheduleNoteSection = briefing?.schedule_note && (
    <div style={{
      ...cardStyle,
      display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
      padding: isMobile ? '0.85rem' : '1rem 1.25rem',
      marginBottom: isMobile ? '0.75rem' : '1rem',
      borderLeft: `4px solid ${brand.tealBlue}`,
      background: 'rgba(0,105,166,0.06)',
    }}>
      <i className="bi bi-lightbulb-fill" style={{ color: brand.goldenrod, fontSize: '1.1rem', flexShrink: 0, marginTop: 2 }} />
      <div style={{
        color: brand.deepBlue, fontSize: isMobile ? '0.92rem' : '0.95rem',
        lineHeight: 1.5, fontWeight: 600, minWidth: 0, ...WRAP,
      }}>
        {briefing.schedule_note}
      </div>
    </div>
  );

  /* A token for WRITING to the mailbox.
   *
   * Separate from the briefing's token on purpose: Mail.ReadWrite is a different
   * scope, and asking for it at page load would prompt every reader of the
   * briefing for write access they may never use. Silent first — most people will
   * already have consented — then interactive, which is allowed here because
   * every path into this function starts with a click.
   */
  const writeToken = useCallback(async () => {
    const account = resolveAccount();
    // Filing into a SHARED mailbox needs Mail.ReadWrite.Shared, which is again
    // its own consent — so it is asked for only while a shared mailbox is
    // selected. Somebody who files only their own mail is never prompted for
    // write access to anyone else's.
    // Demo mode files nothing — `move_message` is answered from fixtures — so
    // the shared WRITE scope would be a consent prompt for access to other
    // people's mail, asked on stage, for a token that moves no message.
    const scopes = (mailbox && !demoMode) ? graphSharedMailWriteRequest : graphMailWriteRequest;
    const req = account
      ? { ...scopes, account }
      : { ...scopes, loginHint: hubEmail() };
    try {
      const r = await msalInstance.acquireTokenSilent(req);
      return r.accessToken;
    } catch (e) {
      if (isMobile) {
        // Popups are unreliable on iOS Safari; the redirect leaves and comes back
        // through /auth-redirect, which returns here via the breadcrumb.
        setMsalReturnTo(PATH);
        await msalInstance.acquireTokenRedirect(req);
        return null;
      }
      const r = await msalInstance.acquireTokenPopup(req);
      return r.accessToken;
    }
  }, [isMobile, mailbox, demoMode]);

  // Folders are fetched on first use of the control rather than at page load, so
  // the write-scope consent prompt only ever appears for someone actually filing
  // a message.
  const ensureFolders = useCallback(async () => {
    // `foldersRes` follows the last mailbox loaded, so the guard has to compare
    // mailboxes and not just "have we ever loaded folders" — otherwise the first
    // picker opened in a shared mailbox would offer the reader's own folders as
    // move targets, and the move would 404 against an id from another mailbox.
    if (foldersRes.isFetching) return;
    if (foldersRes.data && (foldersRes.data.mailbox || '') === mailbox) return;
    setFileError('');
    try {
      const token = await writeToken();
      if (token) await loadFolders({ graphToken: token, mailbox: mailbox || null }, true).unwrap();
    } catch (e) {
      setFileError(e?.data?.detail || 'Your mail folders could not be loaded.');
    }
  }, [foldersRes.data, foldersRes.isFetching, loadFolders, writeToken, mailbox]);

  const fileMessage = useCallback(async (m, destinationId, folderLabel) => {
    if (!destinationId) return;
    setFiling(m.id);
    setFileError('');
    try {
      const token = await writeToken();
      if (!token) return;                       // mobile redirect is in flight
      const res = await moveMessageReq({
        graphToken: token, messageId: m.id, destinationId, mailbox: mailbox || null,
      }).unwrap();
      setFiled(prev => ({
        ...prev,
        [m.id]: {
          newId: res.id,
          folder: res.moved_to || folderLabel || 'the folder',
          // Remembered so Undo can target the right mailbox even if the reader
          // has switched away since.
          mailbox,
        },
      }));
    } catch (e) {
      setFileError(e?.data?.detail || 'That message could not be moved.');
    } finally {
      setFiling('');
    }
  }, [moveMessageReq, writeToken, mailbox]);

  // Undo sends the message back to the Inbox by its well-known name, using the
  // NEW id — the original one died with the move.
  const markDone = useCallback(async (m) => {
    if (!m?.id) return;
    setDismissing(m.id);
    try {
      await dismissReq({ messageId: m.id }).unwrap();
      setDone((prev) => ({ ...prev, [m.id]: true }));
    } catch (e) {
      // Left visible on failure rather than optimistically hidden: a row that
      // disappears and then comes back tomorrow is worse than one that says it
      // could not be dismissed.
      setFileError(e?.data?.detail || 'That message could not be marked done.');
    } finally {
      setDismissing(null);
    }
  }, [dismissReq]);

  const undoDone = useCallback(async (id) => {
    setDismissing(id);
    try {
      await undismissReq({ messageId: id }).unwrap();
      setDone((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch (e) {
      setFileError(e?.data?.detail || 'That message could not be restored.');
    } finally {
      setDismissing(null);
    }
  }, [undismissReq]);

  const unfileMessage = useCallback(async (oldId) => {
    const rec = filed[oldId];
    if (!rec?.newId) return;
    setFiling(oldId);
    setFileError('');
    try {
      const token = await writeToken();
      if (!token) return;
      await moveMessageReq({
        graphToken: token, messageId: rec.newId, destinationId: 'inbox',
        // The mailbox it was filed IN, captured with the move, not the one
        // selected now — an undo pressed after switching mailboxes has to put
        // the message back where it came from.
        mailbox: rec.mailbox || null,
      }).unwrap();
      setFiled((prev) => {
        const next = { ...prev };
        delete next[oldId];
        return next;
      });
    } catch (e) {
      setFileError(e?.data?.detail || 'That message could not be moved back.');
    } finally {
      setFiling('');
    }
  }, [filed, moveMessageReq, writeToken]);

  // One banner for the whole filing feature. Errors here are per-action, not
  // per-page, so it sits above the mail rather than replacing any of it.
  const fileErrorBanner = fileError ? (
    <Alert variant="warning" className="py-2" style={{ fontSize: '0.84rem' }}
      dismissible onClose={() => setFileError('')}>
      {fileError}
    </Alert>
  ) : null;

  const mailRow = (m, tierKey, opts = {}) => {
    // Marked done. Same treatment as a filed row and for the same reason — it
    // holds its place so the Undo is where the eye already is. The difference
    // is what happens next: this one is gone from tomorrow's briefing, and its
    // slot goes to whatever was below the cut line.
    if (done[m.id]) {
      return (
        <div key={m.id} style={{
          padding: isMobile ? '0.7rem 0' : '0.6rem 0',
          borderTop: `1px solid ${brand.softGray}`,
          display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap',
          color: brand.charcoal, fontSize: '0.84rem', ...WRAP,
        }}>
          <i className="bi bi-check2-circle" style={{ color: brand.green }} />
          <span style={WRAP}>Done — won&apos;t appear in future briefings</span>
          <button
            type="button"
            onClick={() => undoDone(m.id)}
            disabled={dismissing === m.id}
            style={{
              background: 'none', border: 'none', padding: 0,
              minHeight: isMobile ? TAP : 'auto',
              color: brand.tealBlue, fontWeight: 700, fontSize: '0.82rem',
              textDecoration: 'underline', cursor: 'pointer',
            }}
          >
            {dismissing === m.id ? 'Restoring…' : 'Undo'}
          </button>
        </div>
      );
    }
    const rec = filed[m.id];
    // A filed message keeps its place in the list rather than vanishing, so the
    // Undo is where the eye already is and nothing silently disappears mid-read.
    if (rec) {
      return (
        <div key={m.id} style={{
          padding: isMobile ? '0.7rem 0' : '0.6rem 0',
          borderTop: `1px solid ${brand.softGray}`,
          display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap',
          color: brand.charcoal, fontSize: '0.84rem', ...WRAP,
        }}>
          <i className="bi bi-check-circle-fill" style={{ color: brand.green }} />
          <span style={WRAP}>Filed in <strong>{rec.folder}</strong></span>
          <button
            type="button"
            onClick={() => unfileMessage(m.id)}
            disabled={filing === m.id}
            style={{
              background: 'none', border: 'none', padding: 0,
              minHeight: isMobile ? TAP : 'auto',
              color: brand.tealBlue, fontWeight: 700, fontSize: '0.82rem',
              textDecoration: 'underline', cursor: 'pointer',
            }}
          >
            {filing === m.id ? 'Moving…' : 'Undo'}
          </button>
        </div>
      );
    }
    return (
    <div key={m.id || `${tierKey}-${m.subject}`} style={{
      padding: isMobile ? '0.7rem 0' : '0.6rem 0',
      borderTop: `1px solid ${brand.softGray}`,
      ...WRAP,
    }}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', flexWrap: 'wrap', ...WRAP }}>
        {/* The tier this row landed in, as a dot. Only drawn where the row is
            NOT already under a tier heading — on the Slack tab, which regroups
            by conversation and would otherwise lose the judgement the briefing
            made. On the Day tab the heading says it, so the dot would be a
            second answer to a question already answered. */}
        {opts.showTier && (
          <span
            title={TIER_LABELS[m.tier] || 'Not sorted'}
            aria-label={TIER_LABELS[m.tier] || 'Not sorted'}
            style={{
              width: 8, height: 8, borderRadius: 8, flexShrink: 0,
              alignSelf: 'center',
              background: TIER_DOTS[m.tier] || brand.charcoal,
            }}
          />
        )}
        {/* Suppressed under a heading that already names them — a one-to-one DM
            on the Slack tab, where every row is from the same person and
            repeating it turns a conversation into a list of nameplates. Never
            suppressed in a channel: one person may happen to be the only poster
            and their name still carries who is asking. */}
        {!opts.hideSender && (
          <span style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '0.88rem', ...WRAP }}>{m.sender_name}</span>
        )}
        <span style={{ color: brand.charcoal, fontSize: '0.75rem', opacity: 0.85, ...WRAP }}>{m.received_label}</span>
        {/* Which app this arrived in. Only Slack is badged: mail is the
            overwhelming majority and the unmarked default, so an "Email" chip
            on every row would be noise on the page and would make the Slack
            ones harder to spot rather than easier.

            Suppressed on the Slack tab, where every row is Slack and the
            conversation is already the heading above it. */}
        {isSlack(m) && !opts.hideSourceChip && (
          <span
            title={SLACK_REASONS[m.reason] || 'From Slack'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              background: 'rgba(0,105,166,0.1)', border: `1px solid ${brand.tealBlue}`,
              color: brand.tealBlue, borderRadius: 4, padding: '0 0.3rem',
              fontSize: '0.7rem', fontWeight: 700, ...WRAP,
            }}
          >
            <i className="bi bi-slack" style={{ fontSize: '0.7rem' }} />
            {m.channel_label || 'Slack'}
          </span>
        )}
        {m.has_attachments && <i className="bi bi-paperclip" style={{ color: brand.charcoal, fontSize: '0.8rem' }} />}
        {/* Only mail that has actually been filed carries a folder. Inbox mail
            has none, so the common case shows no chip at all rather than a
            "Inbox" label on every single row. */}
        {m.folder && (
          <span
            title={`Filed in ${m.folder}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
              background: brand.softGray, color: brand.charcoal,
              borderRadius: 4, padding: '0 0.3rem',
              fontSize: '0.72rem', fontWeight: 700, ...WRAP,
            }}
          >
            <i className="bi bi-folder-fill" style={{ fontSize: '0.7rem' }} />{m.folder}
          </span>
        )}
        {/* Shown so the tiering is explicable: it's why an item is low-priority,
            or why a Tier 1 note says "still owed X" instead of "reply". */}
        {m.replied && (
          <span style={{
            background: 'rgba(26,127,78,0.12)', border: `1px solid ${brand.green}`,
            color: brand.green, borderRadius: 4, padding: '0 0.3rem',
            fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap',
          }}>
            <i className="bi bi-reply-fill" /> Replied
          </span>
        )}
      </div>
      {/* A mail row's identity is its subject. A Slack message has none, so the
          message text takes that slot — without it a Tier 3 Slack row would read
          "Dave Alison — DM with Dave Alison" and carry no information at all.
          The channel is already in the chip above, so it isn't repeated. */}
      <div style={{ color: brand.charcoal, fontSize: '0.88rem', fontWeight: 600, ...WRAP }}>
        {isSlack(m) ? (m.text || '(shared a file)') : m.subject}
      </div>

      {m.summary && (
        <div style={{ color: brand.charcoal, fontSize: '0.86rem', lineHeight: 1.45, marginTop: '0.3rem', ...WRAP }}>
          {m.summary}
        </div>
      )}
      {m.note && (
        <div style={{ color: brand.charcoal, fontSize: '0.84rem', marginTop: '0.25rem', ...WRAP }}>{m.note}</div>
      )}

      {(m.suggested_action || m.deadline || m.web_link || (canFileMail && m.id && !isSlack(m))) && (
        <div style={{
          display: 'flex', gap: '0.4rem', marginTop: '0.5rem',
          flexDirection: isMobile ? 'column' : 'row',
          // Stacked full-width on mobile so each control keeps a thumb-sized target.
          alignItems: isMobile ? 'stretch' : 'center',
          flexWrap: 'wrap',
        }}>
          {m.suggested_action && (
            <span style={{
              background: 'rgba(208,155,44,0.15)', border: `1px solid ${brand.goldenrod}`,
              color: brand.deepBlue, borderRadius: 6, padding: '0.3rem 0.5rem',
              fontSize: '0.8rem', fontWeight: 700, ...WRAP,
            }}>
              {m.suggested_action}
            </span>
          )}
          {m.deadline && (
            <span style={{
              background: 'rgba(192,57,43,0.1)', border: `1px solid ${brand.red}`,
              color: brand.red, borderRadius: 6, padding: '0.3rem 0.5rem',
              fontSize: '0.8rem', fontWeight: 700, ...WRAP,
            }}>
              <i className="bi bi-clock" /> {m.deadline}
            </span>
          )}
          {m.web_link && (
            <a
              href={m.web_link}
              // Slack gets its OWN named tab. Sharing the Outlook one would
              // mean opening a Slack message navigated the mail tab away from
              // the message somebody was part-way through answering.
              target={isSlack(m) ? SLACK_TAB : OUTLOOK_TAB}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                gap: '0.35rem', minHeight: isMobile ? TAP : 32,
                marginLeft: isMobile ? 0 : 'auto',
                padding: '0.3rem 0.7rem', borderRadius: 6,
                border: `1px solid ${brand.tealBlue}`, color: brand.tealBlue,
                background: 'white', fontSize: '0.8rem', fontWeight: 700,
                textDecoration: 'none', whiteSpace: 'nowrap',
              }}
            >
              {isSlack(m) ? 'Open in Slack' : 'Open in Outlook'} <i className="bi bi-box-arrow-up-right" />
            </a>
          )}
          {m.id && (
            <button
              type="button"
              onClick={() => markDone(m)}
              disabled={dismissing === m.id}
              title="Hide this from future briefings. Nothing changes in Outlook."
              style={{
                minHeight: isMobile ? TAP : 32,
                padding: '0.25rem 0.7rem', borderRadius: 6,
                border: `1px solid ${brand.charcoal}`, color: brand.charcoal,
                background: 'white', fontSize: '0.8rem', fontWeight: 700,
                cursor: dismissing === m.id ? 'default' : 'pointer',
              }}
            >
              <i className="bi bi-check2 me-1" />
              {dismissing === m.id ? 'Marking…' : 'Done'}
            </button>
          )}
          {/* Filing is a mailbox move, so it is offered on mail only. Marking
              Done above is hub-side and works for both. */}
          {canFileMail && m.id && !isSlack(m) && (
            <select
              // A native select, not a styled dropdown: it gets keyboard and
              // screen-reader behaviour for free and is the one control pattern
              // that is genuinely pleasant on a phone.
              aria-label="File this message into a folder"
              value=""
              disabled={filing === m.id}
              onFocus={ensureFolders}
              onMouseDown={ensureFolders}
              onChange={(e) => {
                const opt = e.target.selectedOptions[0];
                fileMessage(m, e.target.value, opt?.dataset?.name);
              }}
              style={{
                minHeight: isMobile ? TAP : 32, maxWidth: isMobile ? '100%' : 190,
                padding: '0.25rem 0.5rem', borderRadius: 6,
                border: `1px solid ${brand.charcoal}`, color: brand.charcoal,
                background: 'white', fontSize: '0.8rem', fontWeight: 700,
              }}
            >
              <option value="">
                {filing === m.id ? 'Moving…'
                  : foldersRes.isFetching ? 'Loading folders…' : 'File to…'}
              </option>
              {(foldersRes.data?.folders || []).map(f => (
                <option key={f.id} value={f.id} data-name={f.name}>{f.path}</option>
              ))}
            </select>
          )}
        </div>
      )}
    </div>
    );
  };

  const tierSection = (t) => {
    const rows = briefing?.[t.key] || [];
    const open = tierOpen(t);
    return (
      <div key={t.key} style={{ ...cardStyle, marginBottom: isMobile ? '0.75rem' : '1rem' }}>
        <button
          type="button"
          onClick={() => toggleTier(t)}
          aria-expanded={open}
          style={{
            width: '100%', minHeight: TAP, display: 'flex', alignItems: 'center', gap: '0.6rem',
            padding: isMobile ? '0.75rem 0.9rem' : '0.85rem 1.25rem',
            background: 'white', border: 'none', borderRadius: 12,
            textAlign: 'left', cursor: 'pointer', ...WRAP,
          }}
        >
          <span style={{
            width: 10, height: 10, borderRadius: '50%',
            background: t.dot, flexShrink: 0,
          }} />
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: 'block', color: brand.deepBlue, fontWeight: 800, fontSize: '0.95rem', ...WRAP }}>
              {t.label}
            </span>
            {!isMobile && (
              <span style={{ display: 'block', color: brand.charcoal, fontSize: '0.78rem', ...WRAP }}>{t.blurb}</span>
            )}
          </span>
          <span style={{
            background: brand.softGray, color: brand.deepBlue, borderRadius: 20,
            padding: '0.1rem 0.55rem', fontSize: '0.8rem', fontWeight: 800, flexShrink: 0,
          }}>
            {rows.filter(r => !filed[r.id] && !done[r.id]).length}
          </span>
          <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ color: brand.charcoal, flexShrink: 0 }} />
        </button>
        {open && (
          <div style={{ padding: isMobile ? '0 0.9rem 0.6rem' : '0 1.25rem 0.8rem' }}>
            {rows.length === 0
              ? <div style={{ color: brand.charcoal, fontSize: '0.86rem', paddingTop: '0.4rem' }}>Nothing here.</div>
              : rows.map(m => mailRow(m, t.key))}
          </div>
        )}
      </div>
    );
  };

  const unclassifiedSection = unclassified.length > 0 && (
    <div style={{ ...cardStyle, marginBottom: isMobile ? '0.75rem' : '1rem', borderLeft: `4px solid ${brand.charcoal}` }}>
      <button
        type="button"
        onClick={() => setShowUnclassified(v => !v)}
        aria-expanded={showUnclassified}
        style={{
          width: '100%', minHeight: TAP, display: 'flex', alignItems: 'center', gap: '0.6rem',
          padding: isMobile ? '0.75rem 0.9rem' : '0.85rem 1.25rem',
          background: 'white', border: 'none', borderRadius: 12, textAlign: 'left', cursor: 'pointer',
        }}
      >
        <i className="bi bi-question-circle" style={{ color: brand.charcoal, flexShrink: 0 }} />
        <span style={{ minWidth: 0, flex: 1, color: brand.deepBlue, fontWeight: 700, fontSize: '0.9rem', ...WRAP }}>
          Not sorted ({unclassified.length}) — review these yourself
        </span>
        <i className={`bi bi-chevron-${showUnclassified ? 'up' : 'down'}`} style={{ color: brand.charcoal, flexShrink: 0 }} />
      </button>
      {showUnclassified && (
        <div style={{ padding: isMobile ? '0 0.9rem 0.6rem' : '0 1.25rem 0.8rem' }}>
          {unclassified.map(m => mailRow(m, 'unclassified'))}
        </div>
      )}
    </div>
  );

  /* The Slack tab.
   *
   * Grouped by conversation the way Slack is, because this tab answers a
   * different question from the tiers: "what has happened in Slack" is a browse,
   * and a browse organised by urgency reads as an argument. The tier survives as
   * the dot on each row, so nothing the briefing decided is thrown away.
   *
   * Conversations are ordered newest-first so the live ones lead, but messages
   * WITHIN a conversation run oldest-first — read forwards a conversation is a
   * story, read backwards it's a puzzle. Same reasoning as `group_threads` in
   * bedrock_briefing.py, and the same ordering, so the two views of one thread
   * never contradict each other.
   */
  const slackSection = (section) => {
    const rows = slackRows.filter((r) => (r.reason || 'mention') === section.key);
    if (!rows.length) return null;

    // channel_label is the conversation: "DM with Dave Alison", "#client-service".
    const byConversation = new Map();
    for (const r of rows) {
      const key = r.channel_label || 'Slack';
      if (!byConversation.has(key)) byConversation.set(key, []);
      byConversation.get(key).push(r);
    }
    const conversations = [...byConversation.entries()]
      .map(([label, items]) => ({
        label,
        items: [...items].sort((a, b) => (a.received || '').localeCompare(b.received || '')),
        newest: items.reduce((acc, i) => (i.received > acc ? i.received : acc), ''),
        senders: new Set(items.map((i) => i.sender_name)),
      }))
      .sort((a, b) => (b.newest || '').localeCompare(a.newest || ''));

    return (
      <div key={section.key} style={{ ...cardStyle, marginBottom: isMobile ? '0.75rem' : '1rem' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.6rem',
          padding: isMobile ? '0.75rem 0.9rem 0.4rem' : '0.85rem 1.25rem 0.5rem', ...WRAP,
        }}>
          <i className={`bi ${section.icon}`} style={{ color: brand.tealBlue, fontSize: '1rem', flexShrink: 0 }} />
          <span style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '0.95rem', ...WRAP }}>
            {section.label}
          </span>
          <span style={{ color: brand.charcoal, fontSize: '0.78rem', opacity: 0.9, ...WRAP }}>
            {section.blurb}
          </span>
          <span style={{ marginLeft: 'auto', color: brand.charcoal, fontSize: '0.8rem', fontWeight: 700 }}>
            {rows.length}
          </span>
        </div>
        <div style={{ padding: isMobile ? '0 0.9rem 0.6rem' : '0 1.25rem 0.9rem' }}>
          {conversations.map((c) => (
            <div key={c.label} style={{ marginTop: '0.5rem' }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: '0.4rem',
                color: brand.deepBlue, fontWeight: 700, fontSize: '0.85rem', ...WRAP,
              }}>
                {/* Under "Direct messages", repeating "DM with" on every heading
                    says nothing the section hasn't. The person's name is the
                    conversation. */}
                <span style={WRAP}>
                  {section.key === 'dm' ? c.label.replace(/^DM with /, '') : c.label}
                </span>
                {c.items.length > 1 && (
                  <span style={{ color: brand.charcoal, fontWeight: 600, fontSize: '0.75rem' }}>
                    {c.items.length} messages
                  </span>
                )}
              </div>
              {c.items.map((m) => mailRow(m, `slack-${section.key}`, {
                showTier: true,
                hideSourceChip: true,
                // Only in a one-to-one DM, and only when the heading really does
                // name the sole sender — a group DM has several people in it and
                // needs every line attributed.
                hideSender: section.key === 'dm' && c.senders.size === 1,
              }))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const slackPanel = (() => {
    // The tab is only mounted for a connected account, so the states left are
    // "still generating", "the read failed", and "nothing arrived".
    if (isFetching || phase === 'loading' || (!data && !isError)) {
      return (
        <div style={{ ...cardStyle, padding: isMobile ? '1.5rem' : '2rem', textAlign: 'center' }}>
          <Spinner animation="border" style={{ color: brand.tealBlue }} />
          <div style={{ color: brand.deepBlue, fontWeight: 700, marginTop: '0.75rem', ...WRAP }}>
            Reading Slack…
          </div>
          <div style={{ color: brand.charcoal, fontSize: '0.85rem', marginTop: '0.25rem', ...WRAP }}>
            It arrives with your briefing — one read, not two.
          </div>
        </div>
      );
    }
    if (slackRead.error) {
      return (
        <div style={{ ...cardStyle, padding: isMobile ? '1.5rem' : '2rem', textAlign: 'center' }}>
          <i className="bi bi-slack" style={{ color: brand.charcoal, fontSize: '2rem' }} />
          <h5 style={{ color: brand.deepBlue, fontWeight: 800, marginTop: '0.75rem', ...WRAP }}>
            {slackNeedsReconnect ? 'Slack needs reconnecting' : 'Slack couldn’t be read'}
          </h5>
          <p style={{ color: brand.charcoal, fontSize: '0.9rem', margin: 0, ...WRAP }}>
            {slackNeedsReconnect
              ? 'The connection was revoked in Slack. Reconnect above to bring it back.'
              : 'This is usually temporary — refresh in a moment. Your email briefing is unaffected.'}
          </p>
        </div>
      );
    }
    if (!slackRows.length) {
      return (
        <div style={{ ...cardStyle, padding: isMobile ? '1.5rem' : '2rem', textAlign: 'center' }}>
          <i className="bi bi-slack" style={{ color: brand.goldenrod, fontSize: '2rem' }} />
          <h5 style={{ color: brand.deepBlue, fontWeight: 800, marginTop: '0.75rem', ...WRAP }}>
            Nothing in Slack {data?.coverage_label || ''}
          </h5>
          <p style={{ color: brand.charcoal, fontSize: '0.9rem', margin: 0, ...WRAP }}>
            No direct messages, mentions, or replies in your threads.
          </p>
        </div>
      );
    }
    return (
      <>
        {SLACK_SECTIONS.map(slackSection)}
        {/* Said once, at the bottom, rather than defended on every row: this
            tab shows the same messages as the priorities, not extra ones. */}
        <div style={{ color: brand.charcoal, fontSize: '0.78rem', textAlign: 'center', ...WRAP }}>
          The same messages are sorted into your priorities on Your Day
          {slackRead.partial ? ' · part of Slack couldn’t be read this time' : ''}
        </div>
      </>
    );
  })();

  // ── Tabs ─────────────────────────────────────────────────────────────────

  /* A tab only appears when its feature is actually available.
   *
   * Both admin switches are in the app-wide config query, which has almost always
   * resolved by now — so a firm with Week off never sees the tab appear and then
   * vanish when the 404 lands. `=== false` rather than a truthiness test on
   * purpose: an unloaded config must not hide a tab that works. The response-shaped
   * checks stay as the backstop for everything the flags can't know — the Week
   * call 404ing, or outright failing.
   */
  const flags = adminConfig?.config;
  const weekTabShown = !weekOff && flags?.enable_week_review !== false;
  // Tasks is always available here: it manages its own token and its own error
  // states, so unlike Week and the old Bloom tab there is no config flag or
  // response shape that should make it disappear.
  /* Slack's tab: off by default, and gated three ways.
   *
   * `enable_slack_tab` is `=== true`, the opposite polarity to the switches
   * around it, because this one DEFAULTS OFF — `!== false` would flash the tab
   * on every page load until the config query landed, which is worse than a tab
   * appearing a moment late.
   *
   * It also needs a connected account: an empty tab for something you have
   * never connected is a dead end, and the header is already where connecting
   * is offered. `!mailbox` rides along in slackAvailable, so the tab does not
   * linger while a shared mailbox is selected promising a view that would be
   * empty for a reason nothing on it explains.
   */
  const slackTabShown = slackAvailable && slackConnected && flags?.enable_slack_tab === true;
  const tabAvailable = { day: true, week: weekTabShown, slack: slackTabShown, tasks: true };
  const visibleTabs = TABS.filter((t) => tabAvailable[t.key]);
  // The current tab can be taken out from under us — a refresh that comes back
  // Week switched off server-side while that tab is open. Fall back rather than render
  // an empty page against a tab that no longer exists.
  const activeTab = visibleTabs.some((t) => t.key === tab) ? tab : 'day';

  const tier1Count = (briefing?.tier1 || []).filter((r) => !filed[r.id] && !done[r.id]).length;

  /* A count on a tab you aren't looking at.
   *
   * The whole risk of hiding two thirds of the page behind tabs is that people
   * stop knowing the other thirds exist. The numbers are the answer: Tier 1 in
   * red because it's the one thing on this page that genuinely can't wait, the
   * rest muted. `sr` carries the meaning the digit alone doesn't have.
   */
  const tabBadge = (content, { active, urgent = false, sr }) => {
    let toneKey = 'quiet';
    if (active) toneKey = 'active';
    else if (urgent) toneKey = 'urgent';
    const tone = BADGE_TONES[toneKey];
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 20, borderRadius: 20, padding: '0 0.35rem', flexShrink: 0,
        fontSize: '0.72rem', fontWeight: 800, lineHeight: '1.15rem',
        background: tone.background, color: tone.color,
        border: `1px solid ${tone.borderColor}`,
      }}>
        {content}
        {sr && <span className="visually-hidden"> {sr}</span>}
      </span>
    );
  };

  const tabMark = (key, active) => {
    if (key === 'day') {
      return tier1Count > 0
        ? tabBadge(tier1Count, { active, urgent: true, sr: 'needing your attention' })
        : null;
    }
    if (key === 'week') {
      if (weekLoading) {
        return <Spinner animation="border" size="sm" style={{ width: '0.7rem', height: '0.7rem', flexShrink: 0 }} />;
      }
      const themes = weekPayload?.counts?.themes;
      return themes ? tabBadge(themes, { active, sr: 'themes' }) : null;
    }
    if (key === 'slack') {
      // The total, quiet — never the urgent tone. Anything in Slack that
      // genuinely can't wait is already counted in red on the Day tab, and two
      // red numbers for one message would double-count the morning.
      const n = slackRows.filter((r) => !done[r.id]).length;
      return n ? tabBadge(n, { active, sr: 'Slack messages' }) : null;
    }
    // No badge on Tasks: the count lives inside the embedded page, and this
    // component would have to duplicate its Graph reads to know it.
    return null;
  };

  /* The Slack strip, under the header and above the tabs.
   *
   * Deliberately NOT a tile: the messages themselves are in the tiers and on
   * the Slack tab, so this carries only what neither of those can — whether
   * Slack is connected, whether the read worked, and how to change either.
   *
   * It names the VIEW it is sitting over, which is why it is built here rather
   * than with the other sections: "included in today's priorities" and
   * "included in this week's review" are different claims, and a single generic
   * line would leave people guessing which pages Slack actually reaches.
   */
  const slackCount = activeTab === 'week'
    ? (weekPayload?.counts?.slack || 0)
    : (counts?.slack || 0);
  // Short, because this now lives in a ~280px column of header small print
  // rather than its own full-width band. Still names the VIEW: which pages
  // Slack reaches is the thing people get wrong about it.
  const slackIncludedLine = {
    day: 'In today’s priorities',
    week: 'In this week’s review',
    slack: 'Connected',
    tasks: 'Connected',
  }[activeTab] || 'Connected';

  /* Slack's state, in the blue header under Refresh.
   *
   * It used to be a full-width strip between the header and the tabs, which
   * cost a whole band of vertical space to tell somebody something that does
   * not change from day to day. Up here it is a line of small print in space
   * the header already occupied, and the band it vacated now holds the Ask box
   * — a question people type is worth more room than a status people read once.
   *
   * The wording still names the VIEW ("today's priorities" / "this week's
   * review"), because Slack reaching one page and not another is the thing
   * people actually get wrong about it.
   */
  const slackHeaderNote = slackAvailable && (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.4rem',
      marginTop: 2, flexWrap: 'wrap',
      justifyContent: isMobile ? 'center' : 'flex-end',
      fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)',
      maxWidth: isMobile ? '100%' : 280,
    }}>
      <i className="bi bi-slack" style={{ color: brand.goldenrod, flexShrink: 0 }} />
      {!slackConnected && (
        <>
          <span>Slack not connected</span>
          <button
            type="button"
            onClick={connectSlack}
            disabled={slackBusy}
            style={{
              background: 'none', border: 'none', padding: 0,
              color: brand.goldenrod, fontWeight: 700, fontSize: '0.72rem',
              textDecoration: 'underline', cursor: slackBusy ? 'default' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {slackBusy ? 'Opening…' : 'Connect'}
          </button>
        </>
      )}
      {slackConnected && slackNeedsReconnect && (
        <>
          {/* Goldenrod, not the page's amber: on deep blue the amber fails
              contrast, and this is the one Slack state somebody must act on. */}
          <span style={{ color: brand.goldenrod, fontWeight: 700 }}>Slack needs reconnecting</span>
          <button
            type="button"
            onClick={connectSlack}
            disabled={slackBusy}
            style={{
              background: 'none', border: 'none', padding: 0,
              color: brand.goldenrod, fontWeight: 700, fontSize: '0.72rem',
              textDecoration: 'underline', cursor: slackBusy ? 'default' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Reconnect
          </button>
        </>
      )}
      {slackConnected && !slackNeedsReconnect && (
        <>
          <span style={WRAP}>
            {slackRead.error === 'unavailable'
              ? 'Slack couldn’t be read'
              : `${slackIncludedLine}${slackCount > 0 ? ` · ${slackCount}` : ''}`}
            {slackRead.partial ? ' · partial' : ''}
          </span>
          <button
            type="button"
            onClick={disconnectSlack}
            disabled={slackBusy}
            style={{
              background: 'none', border: 'none', padding: 0,
              color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem',
              textDecoration: 'underline', cursor: slackBusy ? 'default' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Disconnect
          </button>
        </>
      )}
    </div>
  );

  // Arrow keys move between tabs, which is what a tablist is expected to do
  // and what a row of plain buttons would not.
  const tabRefs = useRef({});
  const onTabKey = (e) => {
    const i = visibleTabs.findIndex((t) => t.key === activeTab);
    const last = visibleTabs.length - 1;
    let next = null;
    if (e.key === 'ArrowRight') next = i >= last ? 0 : i + 1;
    else if (e.key === 'ArrowLeft') next = i <= 0 ? last : i - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    const key = visibleTabs[next].key;
    setTab(key);
    tabRefs.current[key]?.focus();
  };

  /* ── The mailbox menu ────────────────────────────────────────────────────
   *
   * A rail on the left of the day and week views, and a plain <select> on
   * mobile, where 220px of chrome beside a 390px screen isn't a choice worth
   * making.
   *
   * It renders ONLY when there is genuinely something to choose — more than one
   * mailbox, or the consent button that would produce more than one. Somebody
   * with no shared access sees the page exactly as it was before this existed,
   * which is the majority of the firm and the reason the rail isn't simply
   * always there showing one entry.
   *
   * Not shown on Tasks: tasks belong to a person, not a mailbox, and a mailbox
   * selector over them would be a control that does nothing.
   */
  const mailboxes = mailboxesRes.data?.mailboxes || [];
  const canAddMailbox = mailboxesRes.data?.can_add === true;
  // The consent button and a running scan both count as reasons to draw the
  // rail: without the first there is no route from "no shared token" to "a
  // menu", and without the second a first-time visitor watches nothing happen
  // and concludes the feature isn't there.
  const showMailboxRail = sharedMailboxesOn
    && (mailboxes.length > 1 || needsSharedConsent || canAddMailbox || scanning);
  const mailboxRowFor = (addr) => mailboxes.find(
    (m) => m.address === addr || (m.alt || []).includes(addr));
  const currentMailbox = mailboxRowFor(mailbox);
  const mailboxLabel = mailbox ? (currentMailbox?.label || mailbox) : 'Your mailbox';

  /* Which address to SHOW for a mailbox.
   *
   * The canonical address is the mailbox's Entra UPN, because that is what
   * always resolves and what the audit row records. It is not always what
   * anyone recognises: the IT support mailbox is
   * support@prosperitycapitaladvisors.com to the whole firm and
   * support@c2penterprises.com to Entra. Showing the UPN would have people
   * reporting the wrong mailbox as the one they were reading.
   *
   * Falls through to the address itself, so a published or hand-added entry —
   * which has no alias list — still shows something.
   */
  const mailboxDisplay = (addr) => {
    if (!addr) return '';
    const row = mailboxRowFor(addr);
    return (row?.alt || [])[0] || row?.address || addr;
  };

  const mailboxRow = (m) => {
    const active = (m.kind === 'own' ? '' : m.address) === mailbox;
    const unavailable = m.unavailable === true;
    return (
      <div key={m.address || 'own'} style={{ display: 'flex', alignItems: 'stretch', gap: 2 }}>
        <button
          type="button"
          onClick={() => !unavailable && selectMailbox(m.kind === 'own' ? '' : m.address)}
          disabled={unavailable}
          aria-current={active ? 'true' : undefined}
          title={unavailable
            ? `Outlook no longer lets you open ${mailboxDisplay(m.address)}`
            : mailboxDisplay(m.address)}
          style={{
            flex: 1, minWidth: 0, minHeight: TAP, textAlign: 'left',
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.45rem 0.6rem', borderRadius: 8,
            border: active ? `1px solid ${brand.deepBlue}` : '1px solid transparent',
            background: active ? brand.deepBlue : 'transparent',
            color: unavailable ? '#9aa4ae' : (active ? 'white' : brand.deepBlue),
            cursor: unavailable ? 'not-allowed' : 'pointer',
          }}
        >
          <i
            className={`bi ${m.kind === 'own' ? 'bi-person-fill' : 'bi-people-fill'}`}
            style={{
              fontSize: '0.9rem', flexShrink: 0,
              color: unavailable ? '#9aa4ae' : (active ? brand.goldenrod : brand.tealBlue),
            }}
          />
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{
              display: 'block', fontWeight: 700, fontSize: '0.85rem',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {m.label}
            </span>
            <span style={{
              display: 'block', fontSize: '0.7rem',
              color: active ? 'rgba(255,255,255,0.7)' : brand.charcoal,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {unavailable ? 'No longer available' : mailboxDisplay(m.address)}
            </span>
          </span>
        </button>
        {m.removable && (
          <button
            type="button"
            onClick={() => removeMailbox(m.address)}
            aria-label={`Remove ${m.address} from your mailbox list`}
            title="Remove from your list (does not change your Outlook access)"
            style={{
              flex: '0 0 auto', width: 30, border: 'none', background: 'transparent',
              color: brand.charcoal, cursor: 'pointer', borderRadius: 8,
            }}
          >
            <i className="bi bi-x" />
          </button>
        )}
      </div>
    );
  };

  const addMailboxForm = (
    <div style={{ marginTop: '0.4rem' }}>
      {showAddMailbox ? (
        <>
          <input
            type="email"
            value={newMailbox}
            onChange={(e) => setNewMailbox(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addMailbox(); }}
            placeholder="service@prosperitycapitaladvisors.com"
            aria-label="Mailbox address to add"
            style={{
              width: '100%', minHeight: TAP, padding: '0.4rem 0.55rem',
              border: '1px solid #cdd5dd', borderRadius: 8,
              fontSize: '0.82rem', color: brand.deepBlue,
            }}
          />
          <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.35rem' }}>
            <button
              type="button"
              onClick={addMailbox}
              disabled={savingMailbox || !newMailbox.trim()}
              style={{
                flex: 1, minHeight: 34, border: 'none', borderRadius: 8,
                background: brand.deepBlue, color: 'white',
                fontWeight: 700, fontSize: '0.8rem',
                cursor: savingMailbox ? 'wait' : 'pointer',
              }}
            >
              {savingMailbox ? 'Checking…' : 'Add'}
            </button>
            <button
              type="button"
              onClick={() => { setShowAddMailbox(false); setNewMailbox(''); setMailboxError(''); }}
              style={{
                flex: '0 0 auto', minHeight: 34, padding: '0 0.6rem',
                border: `1px solid ${brand.softGray}`, borderRadius: 8,
                background: 'white', color: brand.charcoal, fontSize: '0.8rem', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
          {/* Said plainly, because the opposite assumption is the natural one:
              people expect an app that lists a mailbox to have granted it. */}
          <div style={{ color: brand.charcoal, fontSize: '0.7rem', marginTop: '0.35rem', ...WRAP }}>
            Your mailboxes are found automatically — you only need this for one
            that didn’t show up. Adding it doesn’t grant you access; Outlook
            decides, and it’s checked every time.
          </div>
        </>
      ) : (
        <button
          type="button"
          onClick={() => setShowAddMailbox(true)}
          style={{
            width: '100%', minHeight: 34, border: `1px dashed ${brand.softGray}`,
            borderRadius: 8, background: 'transparent', color: brand.tealBlue,
            fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
          }}
        >
          <i className="bi bi-plus-lg me-1" /> Add one manually
        </button>
      )}
    </div>
  );

  const mailboxPanelBody = (
    <>
      {needsSharedConsent ? (
        <>
          <p style={{ color: brand.charcoal, fontSize: '0.78rem', margin: '0 0 0.5rem', ...WRAP }}>
            Shared and delegated mailboxes need one extra Microsoft permission.
          </p>
          <button
            type="button"
            onClick={async () => {
              setMailboxError('');
              const token = await loadSharedToken({ interactive: true });
              if (token) {
                try {
                  await loadMailboxes({ graphToken: token }, false).unwrap();
                } catch (e) {
                  setMailboxError(e?.data?.detail || 'Your mailboxes could not be loaded.');
                }
              }
            }}
            style={{
              width: '100%', minHeight: TAP, border: 'none', borderRadius: 8,
              background: brand.goldenrod, color: brand.deepBlue,
              fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer',
            }}
          >
            Allow other mailboxes
          </button>
        </>
      ) : mailboxesRes.isFetching ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: brand.charcoal, fontSize: '0.8rem' }}>
          <Spinner animation="border" size="sm" style={{ color: brand.tealBlue }} />
          Finding your mailboxes…
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {mailboxes.map(mailboxRow)}
          </div>
          {/* A first visit has nothing but "Your mailbox" until the probe
              finishes, so say what's happening rather than leaving somebody to
              conclude their shared mailboxes aren't supported. */}
          {scanning && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              color: brand.charcoal, fontSize: '0.75rem', marginTop: '0.45rem',
            }}>
              <Spinner animation="border" size="sm"
                style={{ width: '0.65rem', height: '0.65rem', color: brand.tealBlue }} />
              Checking which mailboxes you can open…
            </div>
          )}
          {!scanning && mailboxesRes.data?.scanned && (
            <button
              type="button"
              onClick={rescan}
              title="Ask Outlook again which mailboxes you can open"
              style={{
                marginTop: '0.45rem', border: 'none', background: 'transparent',
                padding: 0, color: brand.tealBlue, fontSize: '0.72rem',
                fontWeight: 700, cursor: 'pointer', textAlign: 'left',
              }}
            >
              <i className="bi bi-arrow-repeat me-1" />
              Rescan mailboxes
            </button>
          )}
          {canAddMailbox && addMailboxForm}
        </>
      )}
      {mailboxError && (
        <Alert variant="warning" className="py-2 mt-2 mb-0" style={{ fontSize: '0.78rem', ...WRAP }}>
          {mailboxError}
        </Alert>
      )}
    </>
  );

  /* The Ask box, in the band the Slack strip used to occupy.
   *
   * One question, answered from this person's mail, calendar and Slack, with
   * the sources it used listed underneath and clickable. The sources are not
   * decoration: an answer over somebody's client correspondence that cannot be
   * checked reads as authoritative whether or not it is right, so the citation
   * list is the part that makes the feature safe to trust.
   *
   * Sits above the tabs because it is not about any one of them — it searches
   * the whole history, not the briefing window, so it would be wrong to imply
   * it belongs to Your Day.
   */
  const askSourceIcon = {
    mail: 'bi-envelope-fill',
    slack: 'bi-slack',
    event: 'bi-calendar-event-fill',
  };

  /* An answer belongs to the mailbox it was ASKED OF, not the one now selected.
   *
   * Without this, asking a question of your own inbox and then switching to
   * service@ leaves the old answer on screen under the new mailbox's heading —
   * a plausible-looking answer about the wrong mailbox, which is the same
   * failure the Week in Review guards against a few hundred lines up, and the
   * one worth designing out here too. Compared against the mailbox the SERVER
   * echoed back, not the one the request was sent with.
   */
  const shownAnswer = answer && (answer.mailbox || '') === mailbox ? answer : null;

  const askBox = askEnabled && (
    <div style={{
      ...cardStyle,
      padding: isMobile ? '0.75rem 0.9rem' : '0.8rem 1.1rem',
      marginBottom: isMobile ? '0.75rem' : '1rem',
    }}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <i className="bi bi-stars" style={{ color: brand.goldenrod, fontSize: '1rem', flexShrink: 0 }} />
        <input
          type="text"
          value={askDraft}
          onChange={(e) => setAskDraft(e.target.value)}
          // Enter submits, because this is a single-field form and reaching for
          // the mouse to ask a question is friction the feature can't afford.
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitAsk(); } }}
          maxLength={500}
          aria-label="Ask a question about your email, calendar and Slack"
          placeholder={mailbox
            ? `Ask about ${mailboxDisplay(mailbox)}…`
            : 'Ask about your email, calendar and Slack…'}
          disabled={askRes.isLoading}
          style={{
            flex: 1, minWidth: isMobile ? '100%' : 240,
            minHeight: isMobile ? TAP : 36,
            border: `1px solid ${brand.softGray}`, borderRadius: 8,
            padding: '0.35rem 0.6rem', fontSize: '0.9rem', color: brand.deepBlue,
          }}
        />
        <button
          type="button"
          onClick={submitAsk}
          disabled={askRes.isLoading || askDraft.trim().length < 3}
          style={{
            minHeight: isMobile ? TAP : 36,
            width: isMobile ? '100%' : 'auto',
            padding: '0.35rem 1rem', borderRadius: 8, border: 'none',
            background: askDraft.trim().length < 3 ? brand.softGray : brand.deepBlue,
            color: askDraft.trim().length < 3 ? brand.charcoal : 'white',
            fontWeight: 700, fontSize: '0.85rem', whiteSpace: 'nowrap',
            cursor: askRes.isLoading || askDraft.trim().length < 3 ? 'default' : 'pointer',
          }}
        >
          {askRes.isLoading ? 'Searching…' : 'Ask'}
        </button>
      </div>

      {askError && (
        <div style={{ color: brand.red, fontSize: '0.82rem', marginTop: '0.5rem', ...WRAP }}>
          <i className="bi bi-exclamation-circle" /> {askError}
        </div>
      )}

      {askRes.isLoading && (
        <div style={{ color: brand.charcoal, fontSize: '0.8rem', marginTop: '0.5rem', ...WRAP }}>
          <Spinner animation="border" size="sm" style={{ width: '0.7rem', height: '0.7rem' }} />
          {' '}Searching your email, calendar and Slack…
        </div>
      )}

      {shownAnswer && !askRes.isLoading && (
        <div style={{ marginTop: '0.7rem', borderTop: `1px solid ${brand.softGray}`, paddingTop: '0.6rem' }}>
          <div style={{
            display: 'flex', gap: '0.5rem', alignItems: 'baseline', flexWrap: 'wrap',
            marginBottom: '0.35rem',
          }}>
            <span style={{ color: brand.charcoal, fontSize: '0.76rem', fontWeight: 700, ...WRAP }}>
              {shownAnswer.question}
            </span>
            <button
              type="button"
              // Clears the box as well as the answer. "Clear" on a form that
              // leaves the question sitting in the field has only half cleared
              // it, and the next thing anybody does is select-all and delete.
              onClick={() => { setAnswer(null); setAskError(''); setAskDraft(''); }}
              style={{
                marginLeft: 'auto', background: 'none', border: 'none', padding: 0,
                color: brand.charcoal, fontSize: '0.76rem',
                textDecoration: 'underline', cursor: 'pointer',
              }}
            >
              Clear
            </button>
          </div>
          <div style={{
            color: brand.deepBlue, fontSize: '0.9rem', lineHeight: 1.5,
            whiteSpace: 'pre-wrap', ...WRAP,
          }}>
            {shownAnswer.answer}
          </div>

          {/* Named plainly rather than dressed up. A source list under an answer
              is a promise that the answer came from these and nothing else. */}
          {(shownAnswer.sources || []).length > 0 && (
            <div style={{ marginTop: '0.6rem' }}>
              <div style={{
                color: brand.charcoal, fontSize: '0.7rem', fontWeight: 800,
                textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.25rem',
              }}>
                From
              </div>
              {shownAnswer.sources.map((s, i) => (
                <div key={i} style={{
                  display: 'flex', gap: '0.4rem', alignItems: 'baseline',
                  padding: '0.2rem 0', fontSize: '0.82rem', ...WRAP,
                }}>
                  <i
                    className={`bi ${askSourceIcon[s.kind] || 'bi-dot'}`}
                    style={{ color: brand.tealBlue, fontSize: '0.75rem', flexShrink: 0 }}
                  />
                  {/* A meeting has no webLink from this read, so it renders as
                      plain text rather than a link that goes nowhere. */}
                  {s.web_link ? (
                    <a
                      href={s.web_link}
                      target={s.kind === 'slack' ? SLACK_TAB : OUTLOOK_TAB}
                      style={{ color: brand.tealBlue, fontWeight: 700, textDecoration: 'none', ...WRAP }}
                    >
                      {s.title}
                    </a>
                  ) : (
                    <span style={{ color: brand.deepBlue, fontWeight: 700, ...WRAP }}>{s.title}</span>
                  )}
                  <span style={{ color: brand.charcoal, opacity: 0.85, ...WRAP }}>
                    {s.who ? `${s.who} · ` : ''}{s.date_label}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div style={{ color: brand.charcoal, fontSize: '0.72rem', marginTop: '0.5rem', ...WRAP }}>
            {/* Both halves matter. The counts stop "nothing found" reading as a
                broken search, and the caveat is the same one the briefing
                carries — this is previews, and it can be wrong. */}
            Searched {shownAnswer.counts?.searched ?? 0} result
            {(shownAnswer.counts?.searched ?? 0) === 1 ? '' : 's'}
            {shownAnswer.counts?.mail ? ` · ${shownAnswer.counts.mail} email` : ''}
            {shownAnswer.counts?.slack ? ` · ${shownAnswer.counts.slack} Slack` : ''}
            {shownAnswer.counts?.events ? ` · ${shownAnswer.counts.events} meeting` : ''}
            {(shownAnswer.unavailable || []).length > 0
              ? ` · couldn’t search ${shownAnswer.unavailable.join(' or ')}`
              : ''}
            {' · '}AI-generated from message previews · always confirm before acting
          </div>
        </div>
      )}
    </div>
  );

  const mailboxRail = (
    <div style={{ ...cardStyle, padding: '0.75rem', marginBottom: isMobile ? '0.75rem' : 0 }}>
      <div style={{
        color: brand.charcoal, fontSize: '0.68rem', fontWeight: 800,
        textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem',
      }}>
        Mailbox
      </div>
      {mailboxPanelBody}
    </div>
  );

  /* Mobile gets a <select>, not the rail.
   *
   * Same choices, a quarter of the vertical space, and it uses the platform
   * picker people already know. The trade-off is that adding and removing
   * mailboxes isn't available here — those are rare, deliberate actions, and
   * cramming them under a select on a phone would cost more than it's worth.
   */
  const mailboxSelect = (
    <div style={{ ...cardStyle, padding: '0.6rem 0.75rem', marginBottom: '0.75rem' }}>
      <label htmlFor="yd-mailbox" style={{
        display: 'block', color: brand.charcoal, fontSize: '0.68rem', fontWeight: 800,
        textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.3rem',
      }}>
        Mailbox
      </label>
      {/* The consent state reuses the rail's body, which already carries its own
          error line — so the one below is only for the select branch. */}
      {needsSharedConsent ? mailboxPanelBody : (
        <select
          id="yd-mailbox"
          value={mailbox}
          onChange={(e) => selectMailbox(e.target.value)}
          style={{
            width: '100%', minHeight: TAP, border: '1px solid #cdd5dd', borderRadius: 8,
            padding: '0.4rem 0.55rem', fontSize: '0.9rem', fontWeight: 700,
            color: brand.deepBlue, background: 'white',
          }}
        >
          {mailboxes.map((m) => (
            <option
              key={m.address || 'own'}
              value={m.kind === 'own' ? '' : m.address}
              disabled={m.unavailable === true}
            >
              {m.label}{m.kind === 'own' ? '' : ` — ${mailboxDisplay(m.address)}`}
            </option>
          ))}
        </select>
      )}
      {!needsSharedConsent && mailboxError && (
        <Alert variant="warning" className="py-2 mt-2 mb-0" style={{ fontSize: '0.78rem', ...WRAP }}>
          {mailboxError}
        </Alert>
      )}
    </div>
  );

  const tabStrip = (
    <div
      role="tablist"
      aria-label="Your Day views"
      onKeyDown={onTabKey}
      style={{
        ...cardStyle,
        display: 'flex', gap: '0.25rem', padding: '0.3rem',
        marginBottom: isMobile ? '0.75rem' : '1rem',
      }}
    >
      {visibleTabs.map((t) => {
        const active = t.key === activeTab;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`yd-tab-${t.key}`}
            aria-selected={active}
            aria-controls={`yd-panel-${t.key}`}
            // Only the selected tab is in the tab order; arrows move within.
            tabIndex={active ? 0 : -1}
            ref={(el) => { tabRefs.current[t.key] = el; }}
            onClick={() => setTab(t.key)}
            style={{
              flex: 1, minWidth: 0, minHeight: TAP,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              gap: '0.4rem', padding: '0.4rem 0.5rem',
              borderRadius: 9, border: 'none',
              background: active ? brand.deepBlue : 'transparent',
              color: active ? 'white' : brand.charcoal,
              fontWeight: 800, fontSize: isMobile ? '0.82rem' : '0.9rem',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            <i className={`bi ${t.icon}`} style={{
              fontSize: '0.9rem', flexShrink: 0,
              color: active ? brand.goldenrod : brand.tealBlue,
            }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isMobile ? t.short : t.label}
            </span>
            {tabMark(t.key, active)}
          </button>
        );
      })}
    </div>
  );

  // Week and Tasks each hold a single view, so it gets the full width of the
  // page rather than sitting in a column sized for a layout that isn't there.
  const panel = (key, children) => (
    <div role="tabpanel" id={`yd-panel-${key}`} aria-labelledby={`yd-tab-${key}`}>
      {children}
    </div>
  );

  // ── Body state machine ───────────────────────────────────────────────────

  // The tabs go up as soon as there's a token to fetch with, not once the
  // briefing lands. That's the point of splitting the views: Day renders the
  // moment it's ready while Week is still generating behind a spinner on its own
  // tab, instead of holding the whole page to the speed of the slowest query.
  const tabbed = phase !== 'connect';
  let dayPanel;
  if (phase === 'connect') {
    dayPanel = (
      <div style={{ ...cardStyle, padding: isMobile ? '1.25rem' : '1.75rem', textAlign: 'center' }}>
        <i className="bi bi-envelope-check" style={{ color: brand.tealBlue, fontSize: '2rem' }} />
        <h5 style={{ color: brand.deepBlue, fontWeight: 800, marginTop: '0.75rem' }}>Connect Outlook</h5>
        <p style={{ color: brand.charcoal, fontSize: '0.9rem', maxWidth: 460, margin: '0 auto 1rem', ...WRAP }}>
          Your Day needs read-only access to your own mailbox and calendar to build the briefing.
          It can never send mail or change anything.
        </p>
        {hubEmail() && (
          <p style={{ color: brand.charcoal, fontSize: '0.8rem', marginBottom: '0.75rem', ...WRAP }}>
            Connecting <strong style={{ color: brand.deepBlue }}>{hubEmail()}</strong>
          </p>
        )}
        {needsReload ? (
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              minHeight: TAP, width: isMobile ? '100%' : 'auto',
              padding: '0.6rem 1.4rem', borderRadius: 8, border: 'none',
              background: brand.deepBlue, color: 'white',
              fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer',
            }}
          >
            <i className="bi bi-arrow-clockwise me-1" /> Reload and try again
          </button>
        ) : (
          <button
            type="button"
            onClick={connect}
            disabled={connecting}
            style={{
              minHeight: TAP, width: isMobile ? '100%' : 'auto',
              padding: '0.6rem 1.4rem', borderRadius: 8, border: 'none',
              background: connecting ? brand.softGray : brand.goldenrod,
              color: connecting ? brand.charcoal : brand.deepBlue,
              fontWeight: 800, fontSize: '0.95rem',
              cursor: connecting ? 'default' : 'pointer',
            }}
          >
            {connecting ? 'Waiting for Microsoft…' : 'Connect Outlook'}
          </button>
        )}
        {authError && (
          <Alert variant="warning" className="py-2 mt-3 mb-0" style={{ fontSize: '0.82rem', ...WRAP }}>
            {authError}
          </Alert>
        )}
      </div>
    );
    // `!data` covers the gap between "token acquired" and "request in flight" —
    // without it there's a frame where nothing is loading and nothing has arrived,
    // which would flash the "no new email" empty state at someone who has mail.
  } else if (isFetching || phase === 'loading' || (!data && !isError)) {
    dayPanel = (
      <div style={{ ...cardStyle, padding: isMobile ? '1.5rem' : '2rem', textAlign: 'center' }}>
        <Spinner animation="border" style={{ color: brand.tealBlue }} />
        <div style={{ color: brand.deepBlue, fontWeight: 700, marginTop: '0.75rem', ...WRAP }}>
          {mailbox
            ? `Reading ${mailboxDisplay(mailbox)} and building the briefing…`
            : 'Reading your inbox and building your briefing…'}
        </div>
        <div style={{ color: brand.charcoal, fontSize: '0.85rem', marginTop: '0.25rem' }}>
          This takes a few seconds.
        </div>
      </div>
    );
  } else if (isError) {
    const status = error?.status;
    const detail = error?.data?.detail;
    dayPanel = (
      <div>
        <Alert variant={status === 429 ? 'warning' : 'danger'} className="py-2" style={WRAP}>
          {detail || 'Could not build your briefing right now.'}
        </Alert>
        {status === 403 && (
          <button
            type="button"
            onClick={connect}
            style={{
              minHeight: TAP, width: isMobile ? '100%' : 'auto', padding: '0.6rem 1.4rem',
              borderRadius: 8, border: 'none', background: brand.goldenrod,
              color: brand.deepBlue, fontWeight: 800, cursor: 'pointer',
            }}
          >
            Reconnect Outlook
          </button>
        )}
      </div>
    );
  } else {
    // An empty inbox is only an empty briefing if Slack is empty too — with
    // Slack connected the tiers can be full while `counts.mail` is zero, and
    // showing "No new email" over a Tier 1 DM would be flatly wrong.
    if (!briefing || (counts?.mail === 0 && !counts?.slack)) {
      // Still show the day's schedule — an empty inbox doesn't mean an empty day.
      dayPanel = (
        <>
          <div style={{ ...cardStyle, padding: isMobile ? '1.5rem' : '2rem', textAlign: 'center', marginBottom: isMobile ? '0.75rem' : '1rem' }}>
            <i className="bi bi-cup-hot-fill" style={{ color: brand.goldenrod, fontSize: '2rem' }} />
            <h5 style={{ color: brand.deepBlue, fontWeight: 800, marginTop: '0.75rem', ...WRAP }}>
              {slackConnected ? 'Nothing new' : 'No new email'} {data?.coverage_label || ''}
            </h5>
            <p style={{ color: brand.charcoal, fontSize: '0.9rem', margin: 0 }}>Have a productive day.</p>
          </div>
          {events.length > 0 && scheduleSection}
          {awaitingSection}
          {peopleSection}
        </>
      );
    } else if (isNarrow) {
      // Phone and narrow desktop: one column. Summary and actions lead, because on a
      // small screen there's no peripheral vision — what to act on must not be a
      // scroll away behind forty email rows.
      dayPanel = (
        <>
          {scheduleNoteSection}
          {priorityActionsSection}
          {scheduleSection}
          {awaitingSection}
          {fileErrorBanner}
          {TIERS.map(tierSection)}
          {unclassifiedSection}
          {peopleSection}
        </>
      );
    } else {
      // Wide desktop: the two full-width bands that frame the day come first — the
      // one-sentence read, then what to act on — and only the detail splits into
      // mail-left / calendar-right. The calendar sticks so it stays on screen while
      // the mail scrolls.
      dayPanel = (
        <>
          {scheduleNoteSection}
          {priorityActionsSection}
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {fileErrorBanner}
              {TIERS.map(tierSection)}
              {unclassifiedSection}
            </div>
            <div style={{
              flex: '0 0 340px', width: 340,
              position: 'sticky', top: '1rem',
              maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto',
            }}>
              {scheduleSection}
              {awaitingSection}
              {peopleSection}
            </div>
          </div>
        </>
      );
    }
  }

  /* Only the active panel is mounted.
   *
   * Nothing is lost by unmounting the other two: every piece of state they read —
   * the queries, which themes are expanded — lives up
   * here, not inside the cards. The Connect screen is the one view with no tabs
   * over it, because none of the three has anything to show without a token.
   */
  const panels = (
    <>
      {activeTab === 'day' && panel('day', dayPanel)}
      {activeTab === 'week' && panel('week', renderWeekCard())}
      {activeTab === 'slack' && panel('slack', slackPanel)}
      {/* Embedded, so it drops its own page chrome and inherits this one. */}
      {activeTab === 'tasks' && panel('tasks', <Tasks embedded />)}
    </>
  );

  /* The mailbox menu sits OUTSIDE the tab strip, to the left of it.
   *
   * Deliberately not a fourth tab and not inside the panels: it applies to both
   * Day and Week, so putting it inside either would make it look like it only
   * governed that one. Left of the tabs it reads as what it is — the scope
   * everything to its right is about.
   *
   * Only on Day and Week. Tasks belong to a person, not a mailbox — and so does
   * Slack, which is never read for a mailbox other than the reader's own, so a
   * mailbox picker over it would offer a choice that changes nothing.
   */
  const mailboxScoped = activeTab === 'day' || activeTab === 'week';
  const railVisible = showMailboxRail && mailboxScoped;

  const body = tabbed ? (
    railTooNarrow ? (
      <>
        {railVisible && mailboxSelect}
        {tabStrip}
        {panels}
      </>
    ) : (
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
        {railVisible && (
          <div style={{
            flex: '0 0 220px', width: 220,
            // Sticks so the mailbox you're reading stays named while a long
            // briefing scrolls past it.
            position: 'sticky', top: '1rem',
          }}>
            {mailboxRail}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {tabStrip}
          {panels}
        </div>
      </div>
    )
  ) : dayPanel;

  const canRefresh = phase === 'ready' && !isFetching;

  /* The banner follows the tab.
   *
   * A header that said "Your Day · covering email since 5pm yesterday" over a
   * seven-day review would be describing the wrong thing, and the coverage line
   * is the part people actually check. Before the tabs are up there's only one
   * view, so the gate states keep the day framing.
   */
  /* The eyebrow names the mailbox when it isn't the reader's own.
   *
   * Not a nicety. Every other word on this page ("Your Day", "you replied",
   * "needs your attention") says "yours", so a briefing over service@ under an
   * unchanged banner is actively misleading — and the rail can be scrolled off
   * or replaced by a dropdown. The mailbox is named in the banner, which is
   * always on screen, and again in the footnote under the content.
   */
  const mailboxEyebrow = mailbox ? `Shared Mailbox · ${mailboxLabel}` : null;
  const HEADERS = {
    day: {
      icon: 'bi-sunrise-fill',
      eyebrow: mailboxEyebrow || 'Daily Briefing',
      title: 'At A Glance',
      // Just the date. The coverage window ("covering email since Friday at
      // 5:00 PM") is still stated at the foot of the tab, where it belongs with
      // the message count rather than in the banner.
      sub: data?.today_label || 'Today',
    },
    week: {
      icon: 'bi-journal-text',
      eyebrow: mailboxEyebrow || 'Week in Review',
      title: 'Your Week',
      sub: weekPayload?.period_label || 'The past week of email, summarised',
    },
    slack: {
      icon: 'bi-slack',
      eyebrow: 'DMs, mentions & threads',
      title: 'Slack',
      // The same window the briefing covers, because it is the same read.
      sub: data?.coverage_label
        ? `Everything ${data.coverage_label}`
        : 'Everything since your last briefing window',
    },
    tasks: {
      icon: 'bi-check2-square',
      eyebrow: 'Microsoft To Do & Planner',
      title: 'Tasks',
      sub: 'Live — changes save straight to Microsoft',
    },
  };
  const head = HEADERS[tabbed ? activeTab : 'day'];

  return (
    // No top padding: the Root shell already reserves 3.5rem on mobile for the
    // hamburger, and adding our own would push the header off the fold.
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>

      {/* Header banner */}
      <div style={{
        background: brand.deepBlue, borderRadius: '12px',
        padding: isMobile ? '1.25rem' : '1.75rem 2rem',
        marginBottom: isMobile ? '1rem' : '1.5rem',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: '300px', height: '100%', background: 'linear-gradient(135deg, transparent 40%, rgba(0,105,166,0.3))', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{
          display: 'flex', gap: '1rem',
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          position: 'relative',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 0, flex: isMobile ? 'none' : 1, width: isMobile ? '100%' : 'auto' }}>
            <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.75rem', flexShrink: 0 }}>
              <i className={`bi ${head.icon}`} style={{ color: brand.goldenrod, fontSize: '1.75rem' }} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
                {head.eyebrow}
              </p>
              <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px', fontSize: isMobile ? '1.25rem' : undefined }}>
                {head.title}
              </h3>
              <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', ...WRAP }}>
                {head.sub}
              </span>
            </div>
          </div>
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 4,
            alignItems: isMobile ? 'stretch' : 'flex-end',
            flex: isMobile ? '1 1 100%' : '0 0 auto',
            width: isMobile ? '100%' : 'auto',
          }}>
            <button
              type="button"
              onClick={refresh}
              disabled={!canRefresh}
              style={{
                minHeight: TAP, width: '100%',
                padding: '0.5rem 1rem', borderRadius: 8, border: 'none',
                background: canRefresh ? brand.goldenrod : 'rgba(255,255,255,0.25)',
                color: canRefresh ? brand.deepBlue : 'rgba(255,255,255,0.7)',
                fontWeight: 700, fontSize: '0.88rem',
                cursor: canRefresh ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
              }}
            >
              <i className="bi bi-arrow-clockwise" /> Refresh
            </button>
            {(isFetching || fulfilledTimeStamp) && (
              <span style={{
                color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem',
                whiteSpace: 'nowrap', textAlign: isMobile ? 'center' : 'right',
              }}>
                {isFetching ? 'Updating…' : `Updated ${relativeTime(fulfilledTimeStamp, now)}`}
              </span>
            )}
            {slackHeaderNote}
          </div>
        </div>
      </div>

      {/* Slack sits ABOVE the tabs, not inside a panel.
          It applies to both Your Day and Your Week — one connection feeds both
          — and duplicating it into each panel would put the same control on
          screen twice. Not shown over Tasks, which reads Microsoft only and has
          nothing to do with Slack. */}
      {/* Slack's transient notices, and the Ask box.
          Ask sits above the tabs because it searches the WHOLE history rather
          than the briefing window — putting it inside Your Day would imply it
          only reaches today. Not shown over Tasks, which is a live read of
          Microsoft to-dos and has nothing to search. */}
      {tabbed && (
        <>
          {slackBanner}
          {activeTab !== 'tasks' && askBox}
        </>
      )}

      {body}

      {/* Footnotes belong to the view above them. The day's counts under a week
          review would be describing mail the reader can't see, and neither line
          means anything under Tasks, which is a live read of Microsoft rather
          than anything a model wrote. */}
      {tabbed && activeTab === 'day' && counts && (
        <div style={{ color: brand.charcoal, fontSize: '0.75rem', textAlign: 'center', paddingBottom: '1rem', ...WRAP }}>
          {/* From the response, not the selection: while a switch is in flight
              the counts below still belong to the previous mailbox, and this
              line has to name the one they actually describe. */}
          {data?.mailbox ? `${mailboxDisplay(data.mailbox)} · ` : ''}
          {counts.mail} message{counts.mail === 1 ? '' : 's'} reviewed
          {/* Counted separately from the mail, because the two ceilings are
              separate — "50 messages reviewed" hiding 12 Slack items inside it
              would misreport how much of either was actually read. */}
          {counts.slack > 0 && ` · ${counts.slack} from Slack`}
          {/* Out of the banner, but not thrown away: without the window, a Monday
              briefing holding three days of mail looks like a very busy weekend. */}
          {data?.coverage_label ? ` ${data.coverage_label}` : ''}
          {/* Stated, not silent: mail was withheld and the reader should know. */}
          {counts.muted > 0 && ` · ${counts.muted} muted`}
          {data?.timezone ? ` · times in ${data.timezone.replace(/_/g, ' ')}` : ''}
          {' · '}AI-generated from message previews
          {counts.slack > 0 ? ' and Slack messages' : ''}
          , not full email bodies · always confirm before acting
        </div>
      )}
      {tabbed && activeTab === 'week' && (
        <div style={{ color: brand.charcoal, fontSize: '0.75rem', textAlign: 'center', paddingBottom: '1rem', ...WRAP }}>
          {weekPayload?.mailbox ? `${mailboxDisplay(weekPayload.mailbox)} · ` : ''}
          {weekPayload?.counts?.slack > 0
            ? `${weekPayload.counts.slack} Slack message${weekPayload.counts.slack === 1 ? '' : 's'} included · `
            : ''}
          AI-generated from message previews, not full email bodies · always confirm before acting
        </div>
      )}
    </div>
  );
}
